/**
 * Create Bonus V2 — template definitions, form defaults, per-step validation,
 * payload builder and human-readable summary.
 *
 * The payload keys intentionally match buildBonusPayload() in
 * src/components/createbonusnew/utils/bonusFormUtils.js so the V2 page posts
 * exactly the shapes the Bonus 2.0 backend already accepts on /bonus/create.
 *
 * Backend gotchas encoded here (verified against platform_bonus source):
 * - bonusSubType is ALWAYS sent (["BONUS"], ["FREE_SPIN"] or ["BONUS",
 *   "FREE_SPIN"] for a wallet bonus that also carries spins): a createBonus
 *   guard bug skips the mandatory maxInstantBonus check when bonusSubType is
 *   omitted and the campaign then silently grants ZERO. The sub-type itself
 *   drives nothing — free spins are driven purely by free_spin_config rows.
 * - Free spins can ride along on a wallet bonus (GOODWILL / ENGAGEMENT /
 *   COUPONCODE / REFERRAL) because the spin fan-out sits at the end of the
 *   generic wallet grant path (BonusDao "FREE SPINS EVENT") — ENGAGEMENT
 *   reuses GOODWILL's EventType/SystemTag there, so it gets identical
 *   treatment. They can NEVER ride on a FREE_BET campaign — grantFreeBet()
 *   returns before that block.
 * - FreeSpinService only enforces the min-deposit gate for EventType.DEPOSIT
 *   (and then only when it is > 0); every other event allocates spins
 *   unconditionally.
 * - DIRECT opt-in is dead backend code — never emitted. Goodwill/Engagement
 *   use MANUAL.
 * - ROLL_OVER release only ever pays out for DEPOSIT / GOODWILL / ENGAGEMENT /
 *   COUPONCODE. REGISTER / REFERRAL / CASHBACK are hard-locked to
 *   WAGER_CRITERIA here because rollover silently never releases for them.
 * - DEPOSIT + WAGER grants zero unless wagerBonus > 0 AND maxInstantBonus > 0:
 *   the wizard caps instant% at 99 for deposit match and auto-fills
 *   maxInstantBonus = maxBonus when the operator leaves it blank.
 * - The backend overrides every freeSpinConfig row's freeSpinMinDepositAmount
 *   with body.minDeposit and freeSpinMaxWinAmount with body.maxWinAmount, so
 *   the wizard only offers the shared fields, never per-row variants.
 * - Cashback: cashBackCriteria is locked to PER_BET (CUMULATIVE is a dead
 *   stub) and cashBackFrequency is never read — neither is offered.
 * - SCHEDULED campaigns route allocation by their first tag
 *   (findBonusDetailBySysTag) — an empty tags array makes a dead campaign, so
 *   a non-empty tag is enforced.
 */
import {
  convertStartDatePickerTimeToMySQLTime,
  convertEndDatePickerTimeToMySQLTime,
  isCryptoCurrency,
  regexType,
} from "../../sharedfiles/helper";

export const TEMPLATE_IDS = {
  // Acquisition
  SIGNUP_BONUS: "SIGNUP_BONUS",
  SIGNUP_FREE_SPINS: "SIGNUP_FREE_SPINS",
  REFERRAL_REWARD: "REFERRAL_REWARD",
  REFERRAL_FREE_SPINS: "REFERRAL_FREE_SPINS",
  // Deposit
  DEPOSIT_MATCH: "DEPOSIT_MATCH",
  DEPOSIT_FREE_SPINS: "DEPOSIT_FREE_SPINS",
  DEPOSIT_FREEBET: "DEPOSIT_FREEBET",
  // Bonus codes
  RELOAD_CODE: "RELOAD_CODE",
  RELOAD_CODE_FREE_SPINS: "RELOAD_CODE_FREE_SPINS",
  SIGNUP_CODE: "SIGNUP_CODE",
  SIGNUP_CODE_FREE_SPINS: "SIGNUP_CODE_FREE_SPINS",
  // Manual / CS
  FREEBET_DROP: "FREEBET_DROP",
  GOODWILL_CREDIT: "GOODWILL_CREDIT",
  GOODWILL_FREE_SPINS: "GOODWILL_FREE_SPINS",
  ENGAGEMENT_CREDIT: "ENGAGEMENT_CREDIT",
  ENGAGEMENT_FREE_SPINS: "ENGAGEMENT_FREE_SPINS",
  // Retention & engagement
  SCHEDULED_FREEBET: "SCHEDULED_FREEBET",
  CASHBACK_REAL: "CASHBACK_REAL",
  CASHBACK_BONUS: "CASHBACK_BONUS",
  BET_INSURANCE: "BET_INSURANCE",
};

const T = TEMPLATE_IDS;

export const TEMPLATE_GROUPS = [
  { id: "ACQUISITION", heading: "Acquisition", chip: "Acquisition" },
  { id: "DEPOSIT", heading: "Deposit", chip: "Deposit" },
  { id: "CODES", heading: "Bonus Codes", chip: "Bonus Codes" },
  { id: "MANUAL_CS", heading: "Manual & CS", chip: "Manual & CS" },
  { id: "RETENTION", heading: "Retention & Engagement", chip: "Retention" },
];

export const MODE_BADGES = {
  AUTO: "Automatic",
  MANUAL: "Code/Manual",
};

/**
 * Behavior flags per template, consumed by validation, the payload builder,
 * the summary builder and the step components. `kind` picks the payload
 * shape: FREEBET | MONEY (wallet bonus with a release path) | SPINS |
 * CASHBACK | INSURANCE.
 *
 * Two cross-cutting flags:
 * - `betConditions` — the bonus is spendable on sports, so the operator can
 *   set the bet shape it may be used on: minimum legs (numberOfLegs), minimum
 *   odds (oddsThreshold) and whether those odds are per selection or
 *   cumulative (isOddsThresholdCumulative). Set on every FREE_BET and every
 *   sports-capable wallet template; these templates also get the sport /
 *   country / league / event scope picker.
 * - `freeSpins` — an OPTIONAL free spin package can ride along with the wallet
 *   bonus. Never set on a FREE_BET template: grantFreeBet() returns before the
 *   spin fan-out, so spins on a free bet are silently impossible.
 *
 * One more flag, `spinsOnly`, marks the DEDICATED free spin campaigns
 * that reuse a wallet bonus type (GOODWILL / ENGAGEMENT / COUPONCODE /
 * REFERRAL) but grant NO wallet component at all (INT-10950). They keep
 * `kind: "MONEY"` because
 * everything about how the grant is triggered — the code, the qualifying
 * deposit, the referral percentages, the payment methods — is still the wallet
 * template's; only the reward changes. The payload builder drops every wallet
 * key for them and posts the spin rows alone.
 *
 * Spins-only is real, not cosmetic: BonusDao.allocate() only writes a
 * user_bonus_details row when a leg is > 0 ("if (unlockedBonus > 0 ||
 * lockedBonus > 0)"), and the free-spin fan-out ("FREE SPINS EVENT") sits
 * AFTER that guard, at the end of the same block. With no wallet amount the
 * grant path skips the wallet row and still publishes the spin event, and
 * FreeSpinService then creates the single zero-amount row the spin rows hang
 * off. That is already how SIGNUP_FREE_SPINS / DEPOSIT_FREE_SPINS behave.
 */
export const TEMPLATE_META = {
  [T.SIGNUP_BONUS]: {
    group: "ACQUISITION",
    mode: "AUTO",
    kind: "MONEY",
    bonusType: "REGISTER",
    fixedTags: ["register_dir"],
    rolloverAllowed: false, // rollover never releases for REGISTER
    unlockFields: true, // exposes unlock odds/legs (defaults 1.5 / 4)
    betConditions: true,
  },
  [T.SIGNUP_FREE_SPINS]: {
    group: "ACQUISITION",
    mode: "AUTO",
    kind: "SPINS",
    bonusType: "REGISTER",
    fixedTags: ["register_dir"],
    depositGate: false, // spins allocate unconditionally at registration
  },
  [T.REFERRAL_REWARD]: {
    group: "ACQUISITION",
    mode: "AUTO",
    kind: "MONEY",
    bonusType: "REFERRAL",
    fixedTags: ["register_refer"],
    rolloverAllowed: false, // rollover never releases for REFERRAL
    referral: true,
    minDepositLabel: "Min qualifying deposit (referee)",
    betConditions: true,
    freeSpins: true,
  },
  [T.REFERRAL_FREE_SPINS]: {
    group: "ACQUISITION",
    mode: "AUTO",
    kind: "MONEY",
    bonusType: "REFERRAL",
    fixedTags: ["register_refer"],
    referral: true, // percentages are still posted — createBonus rejects nulls
    minDepositLabel: "Min qualifying deposit (referee)",
    freeSpins: true,
    spinsOnly: true, // no wallet component — the spin rows ARE the reward
  },
  [T.DEPOSIT_MATCH]: {
    group: "DEPOSIT",
    mode: "AUTO",
    kind: "MONEY",
    bonusType: "DEPOSIT",
    depositTagSelect: true,
    depositBand: "qualify",
    percentOfDeposit: true,
    rolloverAllowed: true,
    instantCap99: true, // DEPOSIT+WAGER grants zero when wagerBonus = 0
    betConditions: true,
  },
  [T.DEPOSIT_FREE_SPINS]: {
    group: "DEPOSIT",
    mode: "AUTO",
    kind: "SPINS",
    bonusType: "DEPOSIT",
    depositTagSelect: true,
    depositGate: true, // minDeposit > 0 is the spin trigger gate
  },
  [T.DEPOSIT_FREEBET]: {
    group: "DEPOSIT",
    mode: "AUTO",
    kind: "FREEBET",
    bonusType: "FREE_BET",
    depositTagSelect: true,
    depositBand: "reward",
    grantCapRequired: true, // no other dedupe exists for auto free bets
    betConditions: true,
  },
  [T.RELOAD_CODE]: {
    group: "CODES",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "COUPONCODE",
    fixedTags: ["deposit_reload"],
    depositBand: "qualify",
    percentOfDeposit: true,
    rolloverAllowed: true,
    code: true,
    paymentMethods: true,
    betConditions: true,
    freeSpins: true,
  },
  [T.RELOAD_CODE_FREE_SPINS]: {
    group: "CODES",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "COUPONCODE",
    fixedTags: ["deposit_reload"],
    depositBand: "qualify", // the deposit gate the backend applies to the spins
    code: true,
    paymentMethods: true,
    freeSpins: true,
    spinsOnly: true,
  },
  [T.SIGNUP_CODE]: {
    group: "CODES",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "COUPONCODE",
    fixedTags: ["register_dir"],
    rolloverAllowed: true,
    code: true,
    betConditions: true,
    freeSpins: true,
  },
  [T.SIGNUP_CODE_FREE_SPINS]: {
    group: "CODES",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "COUPONCODE",
    fixedTags: ["register_dir"],
    code: true,
    freeSpins: true,
    spinsOnly: true,
  },
  [T.FREEBET_DROP]: {
    group: "MANUAL_CS",
    mode: "MANUAL",
    kind: "FREEBET",
    bonusType: "FREE_BET",
    fixedTags: [],
    code: true,
    betConditions: true,
  },
  [T.GOODWILL_CREDIT]: {
    group: "MANUAL_CS",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "GOODWILL",
    fixedTags: ["goodwill"],
    rolloverAllowed: true,
    code: true,
    minStake: true, // only bonus type that enforces minStake at bet time
    betConditions: true,
    freeSpins: true,
  },
  [T.GOODWILL_FREE_SPINS]: {
    group: "MANUAL_CS",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "GOODWILL",
    fixedTags: ["goodwill"],
    code: true,
    freeSpins: true,
    spinsOnly: true,
  },
  [T.ENGAGEMENT_CREDIT]: {
    group: "MANUAL_CS",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "ENGAGEMENT",
    fixedTags: ["engagement"],
    rolloverAllowed: true,
    code: true,
    minStake: true, // same as GOODWILL_CREDIT — engine enforces minStake at bet time for GOODWILL and ENGAGEMENT
    betConditions: true,
    freeSpins: true,
  },
  [T.ENGAGEMENT_FREE_SPINS]: {
    group: "MANUAL_CS",
    mode: "MANUAL",
    kind: "MONEY",
    bonusType: "ENGAGEMENT",
    fixedTags: ["engagement"],
    code: true,
    freeSpins: true,
    spinsOnly: true,
  },
  [T.SCHEDULED_FREEBET]: {
    group: "RETENTION",
    mode: "AUTO",
    kind: "FREEBET",
    bonusType: "FREE_BET",
    scheduled: true,
    scheduledTagSelect: true, // scheduler routes by first tag — never empty
    grantCapRequired: true,
    betConditions: true,
  },
  [T.CASHBACK_REAL]: {
    group: "RETENTION",
    mode: "AUTO",
    kind: "CASHBACK",
    bonusType: "CASHBACK",
    fixedTags: ["cash_back"],
    cashBackType: "REAL_CASH",
  },
  [T.CASHBACK_BONUS]: {
    group: "RETENTION",
    mode: "AUTO",
    kind: "CASHBACK",
    bonusType: "CASHBACK",
    fixedTags: ["cash_back"],
    cashBackType: "BONUS_CASH",
  },
  [T.BET_INSURANCE]: {
    group: "RETENTION",
    mode: "AUTO",
    kind: "INSURANCE",
  },
};

export const templateMeta = (t) => TEMPLATE_META[t] || {};
export const isAutoTemplate = (t) => templateMeta(t).mode === "AUTO";
export const isCodeTemplate = (t) => Boolean(templateMeta(t).code);
export const isSpinTemplate = (t) => templateMeta(t).kind === "SPINS";
/**
 * Dedicated free-spin campaigns built on a wallet bonus type (INT-10950):
 * the spin rows are the whole reward, there is no wallet component at all.
 */
export const isSpinsOnlyTemplate = (t) => Boolean(templateMeta(t).spinsOnly);
/** Every template whose reward is free spins and nothing else. */
export const grantsOnlySpins = (t) => isSpinTemplate(t) || isSpinsOnlyTemplate(t);
/** Templates whose reward can carry an OPTIONAL free spin package. */
export const allowsFreeSpins = (t) => Boolean(templateMeta(t).freeSpins);
/** Templates that expose min legs / min odds / odds scope and a sports scope. */
export const hasBetConditions = (t) => Boolean(templateMeta(t).betConditions);

export const TEMPLATES = [
  // ── Acquisition ────────────────────────────────────────────────────────────
  {
    id: T.SIGNUP_BONUS,
    group: "ACQUISITION",
    title: "Signup Bonus",
    tagline: "New players automatically receive bonus money on registration.",
    example:
      "Example: every new registration gets $10 bonus money, released by wagering 20% turnover on 4+ leg accumulators.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.SIGNUP_FREE_SPINS,
    group: "ACQUISITION",
    title: "Signup Free Spins",
    tagline:
      "Automatic: every new registration receives casino free spins. No code.",
    example:
      "Example: 20 free spins of $0.20 on a chosen slot the moment the account is created. No code involved — every new registration qualifies. The code-driven version is Manual Sign-Up Coupon Free Spins.",
    flavor: "FREE SPINS",
  },
  {
    id: T.REFERRAL_REWARD,
    group: "ACQUISITION",
    title: "Referral Reward",
    tagline: "Reward the referrer (and referee) once the invited player deposits.",
    example:
      "Example: when a referred friend deposits $20+, the referrer gets 50% of $10 and the friend 50%.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.REFERRAL_FREE_SPINS,
    group: "ACQUISITION",
    title: "Referral Free Spins",
    tagline:
      "Automatic: a referred friend deposits and free spins are granted — no bonus money, no code.",
    example:
      "Example: a referred friend deposits $20+ and receives 30 free spins of $0.20. Nothing is credited to either wallet.",
    flavor: "FREE SPINS",
  },
  // ── Deposit ────────────────────────────────────────────────────────────────
  {
    id: T.DEPOSIT_MATCH,
    group: "DEPOSIT",
    title: "Deposit Match Bonus",
    tagline: "Match a % of the player's deposit as bonus money with wagering.",
    example:
      "Example: 100% match on a first deposit up to $200, released after wagering 20% turnover.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.DEPOSIT_FREE_SPINS,
    group: "DEPOSIT",
    title: "Deposit Free Spins",
    tagline:
      "Automatic: any qualifying deposit gets casino free spins. No code.",
    example:
      "Example: deposit $25+ (first deposit) and receive 50 free spins of $0.10 on a chosen slot. No code involved — every qualifying deposit fires it. The code-driven version is Manual Deposit Coupon Free Spins.",
    flavor: "FREE SPINS",
  },
  {
    id: T.DEPOSIT_FREEBET,
    group: "DEPOSIT",
    title: "Deposit Free Bet",
    tagline: "Player deposits within a range, gets a free bet.",
    example:
      "Example: deposit $50–$100 (first deposit) and receive a free bet worth 100% of the deposit, up to $100.",
    flavor: "FREE BET",
  },
  // ── Bonus codes ────────────────────────────────────────────────────────────
  {
    id: T.RELOAD_CODE,
    group: "CODES",
    title: "Deposit Reload Code",
    tagline: "Player redeems a code with a reload deposit and gets bonus money.",
    example:
      "Example: code RELOAD50 gives 50% of a $20–$500 deposit as bonus money. One use per player is automatic.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.RELOAD_CODE_FREE_SPINS,
    group: "CODES",
    title: "Manual Deposit Coupon Free Spins",
    tagline:
      "A coupon code the player redeems WITH A DEPOSIT to get free spins — no bonus money.",
    example:
      "Example: code SPINS50 redeemed with a $20+ deposit gives 50 free spins of $0.10. Deposits below the minimum allocate nothing. Not the same as Deposit Free Spins, which needs no code and fires on every qualifying deposit.",
    flavor: "FREE SPINS",
  },
  {
    id: T.SIGNUP_CODE,
    group: "CODES",
    title: "Signup Code",
    tagline: "Player enters a code at registration and gets bonus money.",
    example:
      "Example: code WELCOME10 entered at signup credits $10 bonus money, released by wagering.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.SIGNUP_CODE_FREE_SPINS,
    group: "CODES",
    title: "Manual Sign-Up Coupon Free Spins",
    tagline:
      "A coupon code the player enters AT REGISTRATION to get free spins — no bonus money.",
    example:
      "Example: code SPINWELCOME entered at signup gives 20 free spins of $0.20, with no deposit required. Not the same as Signup Free Spins, which needs no code and fires for every new registration.",
    flavor: "FREE SPINS",
  },
  // ── Manual / CS ────────────────────────────────────────────────────────────
  {
    id: T.FREEBET_DROP,
    group: "MANUAL_CS",
    title: "Free Bet Drop",
    tagline: "Give free bets to specific players via a bonus code.",
    example:
      "Example: create code VIP25 worth a $25 free bet, then hand it to selected players from the Assign Bonus page.",
    flavor: "FREE BET",
  },
  {
    id: T.GOODWILL_CREDIT,
    group: "MANUAL_CS",
    title: "Goodwill Credit",
    tagline: "CS compensation: credit bonus money to specific players.",
    example:
      "Example: code SORRY10 worth $10 bonus money, assigned to affected players via Assign Bonus or bulk CSV.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.GOODWILL_FREE_SPINS,
    group: "MANUAL_CS",
    title: "Goodwill Free Spins",
    tagline:
      "CS compensation paid in free spins: hand a code to specific players, no bonus money.",
    example:
      "Example: code SORRYSPINS worth 25 free spins of $0.20, assigned to affected players via Assign Bonus or bulk CSV. Never granted automatically.",
    flavor: "FREE SPINS",
  },
  {
    id: T.ENGAGEMENT_CREDIT,
    group: "MANUAL_CS",
    title: "Engagement Credit",
    tagline: "Engagement reward: credit bonus money to specific players.",
    example:
      "Example: code ENGAGE10 worth $10 bonus money, assigned to targeted players via Assign Bonus or bulk CSV.",
    flavor: "BONUS MONEY",
  },
  {
    id: T.ENGAGEMENT_FREE_SPINS,
    group: "MANUAL_CS",
    title: "Engagement Free Spins",
    tagline:
      "Engagement reward paid in free spins: hand a code to specific players, no bonus money.",
    example:
      "Example: code ENGAGESPINS worth 25 free spins of $0.20, assigned to targeted players via Assign Bonus or bulk CSV. Never granted automatically.",
    flavor: "FREE SPINS",
  },
  // ── Retention & engagement ─────────────────────────────────────────────────
  {
    id: T.SCHEDULED_FREEBET,
    group: "RETENTION",
    title: "Scheduled / Reactivation Campaign",
    tagline:
      "Automatically reward players matching activity criteria, evaluated daily.",
    example:
      "Example: players inactive for 30+ days automatically receive a $10 free bet valid for 24 hours.",
    flavor: "FREE BET",
  },
  {
    id: T.CASHBACK_REAL,
    group: "RETENTION",
    title: "Cashback — Real Cash",
    tagline: "Give back a % of each lost (or won) bet as withdrawable cash.",
    example:
      "Example: 10% of every lost bet of $5+ back as real cash, from a shared $10,000 cashback pot.",
    flavor: "CASHBACK",
  },
  {
    id: T.CASHBACK_BONUS,
    group: "RETENTION",
    title: "Cashback — Bonus Cash",
    tagline: "Give back a % of each lost (or won) bet as bonus money.",
    example:
      "Example: 15% of every lost bet back as bonus money, released by wagering 20% turnover.",
    flavor: "CASHBACK",
  },
  {
    id: T.BET_INSURANCE,
    group: "RETENTION",
    title: "Bet Insurance (One-Cut / Two-Cut)",
    tagline:
      "Refund a % of the stake when an accumulator loses by one (or two) legs.",
    example:
      "Example: 5+ leg accumulators at odds 1.2+ per leg get 100% of the stake back if exactly one leg lets them down.",
    flavor: "INSURANCE",
  },
].map((tpl) => ({ ...tpl, badge: MODE_BADGES[TEMPLATE_META[tpl.id].mode] }));

// Deposit tag options (subset of the tags the backend understands for
// deposit-triggered campaigns — see createBonusData.js).
export const DEPOSIT_TAG_OPTIONS = [
  { id: 1, label: "First deposit", value: "deposit_first" },
  { id: 2, label: "Second deposit", value: "deposit_second" },
  { id: 3, label: "Third deposit", value: "deposit_third" },
  { id: 4, label: "Fourth deposit", value: "deposit_fourth" },
  { id: 5, label: "Any reload deposit", value: "deposit_reload" },
];

// The scheduler posts systemTag = first tag and the allocation service looks
// the campaign up by that tag — any searchable SystemTag works, but it must
// never be empty.
export const SCHEDULED_TAG_OPTIONS = [
  ...DEPOSIT_TAG_OPTIONS,
  { id: 6, label: "Goodwill / generic", value: "goodwill" },
];

export const CAMPAIGN_CATEGORY_OPTIONS = [
  { id: 1, label: "Reactivation", value: "REACTIVATION" },
  { id: 2, label: "Retention", value: "RETENTION" },
  { id: 3, label: "Event Promo", value: "EVENT_PROMO" },
];

export const EXPIRY_UNIT_OPTIONS = [
  { id: 1, label: "Days", value: "DAYS" },
  { id: 2, label: "Hours", value: "HOURS" },
];

export const CAMPAIGN_DAY_OPTIONS = [
  { label: "Sunday", value: "SUNDAY" },
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
];

export const CASHBACK_BET_STATUS_OPTIONS = [
  { id: 1, label: "Lost bets", value: "Lost" },
  { id: 2, label: "Won bets", value: "Won" },
];

// SPORTS-only bet types — hidden when the cashback applies to casino games.
export const CASHBACK_BET_TYPE_OPTIONS = [
  { id: 1, label: "Single", value: "Single" },
  { id: 2, label: "Multi", value: "Multi" },
  { id: 3, label: "Combi", value: "Combi" },
  { id: 4, label: "System", value: "System" },
  { id: 5, label: "Split", value: "Split" },
  { id: 6, label: "Custom", value: "Custom" },
  { id: 7, label: "Fancy", value: "Fancy" },
];

/**
 * Sports vs Games & Casino for a wallet bonus (INT-10950). SPORTS is the
 * default and behaves exactly as before (applicableType flips ALL → SPORTS
 * as soon as a scope row or market exists). CASINO posts applicableType
 * CASINO plus providerName[] / gameName[] / gameCode[].
 *
 * NE_GAMES is deliberately NOT offered: platform_bonus's own
 * placeBetWithBonus coerces GameType.NE_GAMES to CASINO before looking the
 * user's bonuses up by applicable_type, so a row stamped NE_GAMES can never
 * be matched there — the two betting implementations disagree, so the wizard
 * only offers the value both honour.
 */
export const SCOPE_CATEGORY_OPTIONS = [
  { id: 1, label: "Sports", value: "SPORTS" },
  { id: 2, label: "Games & Casino", value: "CASINO" },
];

/** Casino scope rows are provider+game pairs — see casinoScopeKeys(). */
export const MAX_CASINO_ROWS = 20;

export const APPLICABLE_TYPE_OPTIONS = [
  { id: 1, label: "Everything (global)", value: "ALL" },
  { id: 2, label: "Sports only", value: "SPORTS" },
  { id: 3, label: "Casino only", value: "CASINO" },
  { id: 4, label: "NE Games only", value: "NE_GAMES" },
];

export const INSURANCE_CUT_OPTIONS = [
  { value: "ONECUT", label: "One-Cut", tag: "one_cut" },
  { value: "TWOCUT", label: "Two-Cut", tag: "two_cut" },
];

// How the minimum-odds threshold is read at bet time. CUMULATIVE posts
// isOddsThresholdCumulative: true, which BonusInternalService reads as
// isOddThresholdCumulative — the total odds of the bet must clear the
// threshold instead of every single selection.
export const ODDS_SCOPE_OPTIONS = [
  { id: 1, label: "Each selection", value: "SELECTION" },
  { id: 2, label: "Whole bet (cumulative)", value: "CUMULATIVE" },
];

// Same fallback the old page uses when the brand currencies API returns nothing.
export const FALLBACK_CURRENCIES = [
  { label: "ARS", value: "ARS" },
  { label: "BOB", value: "BOB" },
  { label: "CLP", value: "CLP" },
  { label: "MXN", value: "MXN" },
  { label: "USD", value: "USD" },
  { label: "ADA", value: "ADA" },
  { label: "DAI", value: "DAI" },
  { label: "TRX", value: "TRX" },
  { label: "USDC", value: "USDC" },
  { label: "USDT", value: "USDT" },
  { label: "BRL", value: "BRL" },
];

export const STEP_LABELS = [
  "Campaign type",
  "Who qualifies",
  "The reward",
  "Safety limits",
  "Review & create",
];

// Status choices offered by the edit form. A campaign can also sit in a status
// the operator can't pick (EXPIRED), which statusOptionsFor() keeps visible.
export const BONUS_STATUS_OPTIONS = [
  { id: 1, label: "Active — issuing bonuses", value: "ACTIVE" },
  { id: 2, label: "Inactive — stopped issuing", value: "IN_ACTIVE" },
];

/** Status dropdown options, always including the campaign's current status. */
export const statusOptionsFor = (current) =>
  !current || BONUS_STATUS_OPTIONS.some((opt) => opt.value === current)
    ? BONUS_STATUS_OPTIONS
    : [
      ...BONUS_STATUS_OPTIONS,
      { id: 3, label: `${current} (current)`, value: current },
    ];

export const MAX_FREE_SPIN_ROWS = 6;

export const emptyFreeSpinRow = () => ({
  providerName: "",
  subProviderName: "",
  gameName: "",
  gameId: null,
  gameCode: null,
  freeSpinAmount: "",
  numberOfFreeSpins: "",
});

export const emptyForm = () => ({
  template: null,
  // Edit mode (Bonus List V2 → Edit): true when the wizard is editing a live
  // campaign rather than creating one. Never part of the payload.
  editMode: false,
  // Code the replacement campaign gets when an edit has to replace the
  // original (the backend keeps bonus codes unique across every status).
  replacementBonusCode: "",
  // Basics
  bonusName: "",
  bonusDescription: "",
  currencyCode: "",
  bonusStatus: "ACTIVE",
  startDate: null,
  validUntil: null,
  // Qualifying
  minDeposit: "",
  maxDeposit: "",
  depositTag: "deposit_first",
  bonusCode: "",
  allowTags: [],
  denyTags: [],
  paymentMethods: [],
  inactiveDaysMin: "",
  depositWindowDays: "",
  depositWindowMinAmount: "",
  requireLifetimeDeposit: false,
  casinoLossPercentMin: "",
  casinoWindowDays: "",
  campaignCategory: "REACTIVATION",
  campaignDays: [],
  timeFrom: "",
  timeTo: "",
  // Reward
  bonusPercentage: "100",
  // Free bet amount: match the deposit (default) or grant a fixed amount.
  freeBetAmountMode: "MATCH",
  freeBetFixedAmount: "",
  maxBonus: "",
  oddsThreshold: "1.01",
  // Bet shape the bonus may be spent on (templates with meta.betConditions).
  // Blank legs = no restriction; SELECTION reads oddsThreshold per selection,
  // CUMULATIVE reads it against the total odds of the bet.
  oddsThresholdScope: "SELECTION",
  maxWinPercent: "",
  maxWinAmount: "",
  // Where the bonus can be spent (INT-10950). SPORTS keeps the sport →
  // country → league → event → market hierarchy below; CASINO swaps it for
  // the provider → game picker and posts providerName/gameName/gameCode.
  // Only meta.betConditions wallet templates offer the choice.
  scopeCategory: "SPORTS",
  // Casino scope rows: { providerName, subProviderName, gameName, gameCode }.
  // Empty with scopeCategory CASINO = any casino game.
  casinoRows: [],
  // Scope: rows collected by the cascading dropdowns (sport → country →
  // league → event) and the market-name multi-select. Empty = all sports.
  scopeRows: [],
  markets: [],
  stakeReturned: false,
  // Bonus-money release path
  releaseMode: "WAGER", // WAGER | ROLLOVER (rollover only where it releases)
  instantBonus: "0",
  wagerBonus: "100",
  wagerCriteria: "20",
  rollOverMultiplier: "",
  maxInstantBonus: "",
  unlockOddsThreshold: "",
  unlockNumberOfLegs: "",
  // Referral
  referralAmountMode: "FIXED", // FIXED (maxBonus) | PERCENT (bonusPercentage)
  referrerPercentage: "0",
  recReferrerPercentage: "0",
  refereePercentage: "0",
  maxTotalBonus: "",
  // Goodwill min stake / insurance shape. numberOfLegs is shared: it is the
  // insured accumulator size on BET_INSURANCE and the minimum number of legs
  // a qualifying bet must carry on every meta.betConditions template.
  minStake: "",
  numberOfLegs: "",
  bonusPayoutPercentage: "",
  insuranceCut: "ONECUT",
  // Free spins. On a meta.freeSpins template the package is OPTIONAL and
  // rides along with the wallet bonus (INT-10920); on a meta.spinsOnly or
  // kind SPINS template it IS the reward.
  freeSpinConfig: [],
  // Cashback
  applicableTypeChoice: "ALL",
  cashBackBetStatus: "Lost",
  cashBackPercentage: "",
  maxCashBackAmount: "",
  minStakeAmount: "",
  minCashBackOdds: "",
  minCashBackLegs: "",
  cashBackBetType: [],
  addToWithdrawableAmount: false,
  expiryValue: "7",
  expiryUnit: "DAYS",
  // Safety limits
  maxGrantsPerPlayer: "1",
  maxGrantsPerPlayerPerDay: "1",
  maxCategoryGrants: "2",
  categoryWindowDays: "180",
});

/** Fresh form pre-configured for the chosen template. */
export const formForTemplate = (templateId) => {
  const form = { ...emptyForm(), template: templateId };
  if (templateId === T.SCHEDULED_FREEBET) {
    form.expiryValue = "24";
    form.expiryUnit = "HOURS";
  }
  if (templateId === T.SIGNUP_BONUS) {
    // Classic signup-bonus anti-abuse release defaults.
    form.unlockOddsThreshold = "1.5";
    form.unlockNumberOfLegs = "4";
  }
  if (templateId === T.BET_INSURANCE) {
    form.oddsThreshold = "1.2"; // same default as the classic page
  }
  if (grantsOnlySpins(templateId)) {
    form.freeSpinConfig = [emptyFreeSpinRow()];
  }
  return form;
};

const num = (v) => Number(v);
const filled = (v) => v !== "" && v !== null && v !== undefined;
const positive = (v) => filled(v) && Number(v) > 0;
const pctInvalid = (v) => !filled(v) || Number(v) < 0 || Number(v) > 100;

// ─── Crypto Decimal Amounts ───────────────────────────────────────────────────

// Monetary fields that accept decimal values when a crypto currency (any currency outside MULTIPLIER_100_CURRENCIES) is selected
export const V2_DECIMAL_AMOUNT_FIELDS = [
  "minStake",
  "minStakeAmount",
  "minDeposit",
  "maxDeposit",
  "depositWindowMinAmount",
  "maxInstantBonus",
  "maxWinAmount",
  "freeBetFixedAmount",
  "maxBonus",
  "maxTotalBonus",
  "maxCashBackAmount",
];

const CRYPTO_AMOUNT_MAX_LENGTH = 23;

/**
 * Returns the regex/maxLen for an input. Crypto amount fields get the decimal regex;
 * every other field (and every fiat currency) keeps the values passed in.
 */
export const getAmountInputProps = (name, regex, maxLen, currencyCode) =>
  isCryptoCurrency(currencyCode) && V2_DECIMAL_AMOUNT_FIELDS.includes(name)
    ? { regex: regexType.cryptoAmount, maxLen: CRYPTO_AMOUNT_MAX_LENGTH }
    : { regex, maxLen };

/**
 * When the currency is no longer crypto, decimal amounts typed for crypto are invalid —
 * returns those fields reset to "" so they are re-entered as whole numbers.
 */
export const getDecimalAmountResets = (form, currencyCode) => {
  if (isCryptoCurrency(currencyCode)) return {};
  return V2_DECIMAL_AMOUNT_FIELDS.reduce((acc, name) => {
    if (String(form[name] ?? "").includes(".")) acc[name] = "";
    return acc;
  }, {});
};

/** Cashback templates: is the campaign sports-facing (bet-shaped criteria)? */
export const cashbackSportsFacing = (form) =>
  form.applicableTypeChoice === "ALL" || form.applicableTypeChoice === "SPORTS";

/** Effective release mode — templates without rollover are always WAGER. */
export const effectiveReleaseMode = (form) =>
  templateMeta(form.template).rolloverAllowed ? form.releaseMode : "WAGER";

/**
 * True when the campaign grants free spins and NO wallet bonus at all.
 *
 * This is a property of the TEMPLATE, not something the operator toggles: the
 * four dedicated free spin campaigns built on a wallet bonus type carry
 * meta.spinsOnly (INT-10950). The wallet templates they were split out of are
 * bonus money, optionally with a spin package riding along (INT-10920).
 */
export const isSpinsOnly = (form) => isSpinsOnlyTemplate(form.template);

/**
 * True when the operator scoped the bonus to casino games instead of sports.
 * Only offered on wallet templates (meta.betConditions && kind MONEY) and
 * never on a spins-only campaign, whose spin rows already name the games.
 *
 * Free bets are excluded on purpose: a FREE_BET grant is never selected by
 * BonusInternalService.placeBetWithBonus (it skips bonusType FREE_BET) and
 * validateFreeBet is a sports-only path (sportsIds / oddsList / legs), so a
 * casino-scoped free bet could never be spent.
 */
export const casinoScoped = (form) => {
  const meta = templateMeta(form.template);
  return Boolean(
    meta.kind === "MONEY" &&
      meta.betConditions &&
      !isSpinsOnly(form) &&
      form.scopeCategory === "CASINO"
  );
};

const uniqStrings = (values) => [...new Set(values.filter(Boolean))];

/**
 * Casino scope rows → the three parallel payload lists.
 *
 * providerName and gameName are ALWAYS posted together. The engine's matcher
 * (BonusInternalService.placeBetWithBonus, and BonusDao's unlock twins) reads
 * them as "both empty = no restriction"; with only one of the two filled the
 * other predicate falls through to `list.any { ... }` over an empty list,
 * which is false, and the bonus becomes unusable / unreleasable. That is why
 * every row must name a provider AND a game (validateCasinoRows enforces it).
 *
 * The backend comma-joins each list into a single array element and matches
 * with `contains`, so the joined form still matches every entry.
 */
export const casinoScopeKeys = (rows = []) => ({
  providerName: uniqStrings(rows.map((row) => row.providerName)),
  gameName: uniqStrings(rows.map((row) => row.gameName)),
  gameCode: uniqStrings(rows.map((row) => row.gameCode)),
});

// ─── Per-step validation ─────────────────────────────────────────────────────

const validateDepositBand = (form, minRequired = true) => {
  const errors = {};
  if (minRequired && !positive(form.minDeposit)) {
    errors.minDeposit = "Minimum deposit must be greater than 0.";
  }
  if (
    filled(form.maxDeposit) &&
    filled(form.minDeposit) &&
    num(form.maxDeposit) < num(form.minDeposit)
  ) {
    errors.maxDeposit =
      "Maximum deposit must be greater than or equal to the minimum deposit.";
  }
  return errors;
};

const validateCodeFields = (form, errors) => {
  if (!form.bonusCode.trim()) {
    errors.bonusCode = "A bonus code is required for this campaign type.";
  }
  // Editing a code-based campaign: the original code stays reserved by the
  // original campaign, so a replacement always needs a code of its own.
  if (form.editMode) {
    const replacement = form.replacementBonusCode.trim();
    if (!replacement) {
      errors.replacementBonusCode =
        "A new bonus code is required — the original code stays reserved by the current campaign.";
    } else if (replacement.toLowerCase() === form.bonusCode.trim().toLowerCase()) {
      errors.replacementBonusCode =
        "The new code must be different from the original code.";
    }
  }
};

const validateQualify = (form) => {
  const errors = {};
  const t = form.template;
  const meta = templateMeta(t);
  if (!form.bonusName.trim()) errors.bonusName = "Campaign name is required.";
  if (!form.startDate) errors.startDate = "Start date is required.";
  if (!form.validUntil) errors.validUntil = "End date is required.";
  if (
    form.startDate &&
    form.validUntil &&
    new Date(form.validUntil) < new Date(form.startDate)
  ) {
    errors.validUntil = "End date must be after the start date.";
  }
  if (meta.kind === "FREEBET" && !form.currencyCode) {
    errors.currencyCode = "Currency is required for free bet campaigns.";
  }

  // Deposit-triggered templates: which deposit qualifies. The band itself
  // lives on the Qualify step for some templates and on the Reward step for
  // others (see TEMPLATE_META.depositBand / depositGate).
  if (meta.depositTagSelect && !form.depositTag) {
    errors.depositTag = "Select which deposit qualifies.";
  }
  if (meta.depositBand === "qualify") {
    Object.assign(errors, validateDepositBand(form));
  }
  if (meta.referral && !positive(form.minDeposit)) {
    errors.minDeposit =
      "The referred player's qualifying deposit must be greater than 0.";
  }

  // Scheduler routes allocation by the first tag — an empty tag would create
  // a campaign the daily job can never pay out.
  if (meta.scheduledTagSelect && !form.depositTag) {
    errors.depositTag =
      "A campaign tag is required — the daily scheduler routes grants by it.";
  }

  if (meta.code) validateCodeFields(form, errors);

  if (t === T.BET_INSURANCE) {
    if (!filled(form.oddsThreshold) || Number(form.oddsThreshold) < 1) {
      errors.oddsThreshold = "Minimum odds per leg is required (at least 1.00).";
    }
    if (!filled(form.numberOfLegs) || Number(form.numberOfLegs) < 1) {
      errors.numberOfLegs = "Minimum number of legs is required (at least 1).";
    }
    if (!positive(form.minStake)) {
      errors.minStake = "Minimum stake must be greater than 0.";
    }
  }

  if (meta.kind === "CASHBACK") {
    if (!form.cashBackBetStatus) {
      errors.cashBackBetStatus = "Pick whether won or lost bets earn cashback.";
    }
    if (!positive(form.minStakeAmount)) {
      errors.minStakeAmount = "Minimum stake must be greater than 0.";
    }
    if (cashbackSportsFacing(form)) {
      if (!filled(form.minCashBackOdds) || Number(form.minCashBackOdds) < 1) {
        errors.minCashBackOdds = "Minimum odds is required (at least 1.00).";
      }
      if (!filled(form.minCashBackLegs) || Number(form.minCashBackLegs) < 1) {
        errors.minCashBackLegs = "Minimum legs is required (at least 1).";
      }
      if (!form.cashBackBetType.length) {
        errors.cashBackBetType = "Pick at least one qualifying bet type.";
      }
    }
  }

  if (t === T.SCHEDULED_FREEBET) {
    const hasCriterion =
      filled(form.inactiveDaysMin) ||
      filled(form.depositWindowDays) ||
      filled(form.depositWindowMinAmount) ||
      form.requireLifetimeDeposit ||
      filled(form.casinoLossPercentMin) ||
      filled(form.casinoWindowDays);
    if (!hasCriterion) {
      errors.scheduledCriteria =
        "Add at least one qualifying criterion so the daily scheduler knows who to reward.";
    }
  }
  if (
    (form.timeFrom && !form.timeTo) ||
    (!form.timeFrom && form.timeTo)
  ) {
    errors.timeWindow = "Set both a from and a to time, or leave both empty.";
  }
  return errors;
};

/**
 * Optional bet-shape conditions shared by every meta.betConditions template:
 * minimum legs and how the minimum-odds threshold is read. Both may be left
 * empty — an empty leg count means the bonus is usable on any bet.
 */
const validateBetConditions = (form, errors) => {
  if (filled(form.numberOfLegs) && Number(form.numberOfLegs) < 1) {
    errors.numberOfLegs =
      "Minimum selections must be at least 1 (or leave empty for no restriction).";
  }
  if (filled(form.oddsThreshold) && Number(form.oddsThreshold) < 1) {
    errors.oddsThreshold = "Minimum odds cannot be below 1.00.";
  }
};

const validateFreeSpinRows = (form) => {
  const rows = form.freeSpinConfig || [];
  if (rows.length === 0) {
    return "Complete the free spin row: provider, sub provider, game, spin amount (> 0) and number of spins (≥ 1).";
  }
  if (rows.length > MAX_FREE_SPIN_ROWS) {
    return `At most ${MAX_FREE_SPIN_ROWS} free spin rows are supported.`;
  }
  const incomplete = rows.some(
    (row) =>
      !row.providerName ||
      !row.subProviderName ||
      !row.gameName ||
      !positive(row.freeSpinAmount) ||
      !filled(row.numberOfFreeSpins) ||
      Number(row.numberOfFreeSpins) < 1
  );
  if (incomplete) {
    return "Complete every free spin row: provider, sub provider, game, spin amount (> 0) and number of spins (≥ 1).";
  }
  return "";
};

/**
 * Casino scope rows. Zero rows is valid (= any casino game); every row that
 * IS there must name both a provider and a game, because the engine treats a
 * half-filled restriction as "nothing matches" — see casinoScopeKeys().
 */
const validateCasinoRows = (form) => {
  const rows = form.casinoRows || [];
  if (rows.length > MAX_CASINO_ROWS) {
    return `At most ${MAX_CASINO_ROWS} casino game selections are supported.`;
  }
  const incomplete = rows.some((row) => !row.providerName || !row.gameName);
  if (incomplete) {
    return "Every casino selection needs both a provider and a game — the engine ignores a half-filled restriction and the bonus becomes unusable.";
  }
  return "";
};

const validateMoneyReward = (form, errors) => {
  const meta = templateMeta(form.template);
  if (!positive(form.maxBonus)) {
    errors.maxBonus = meta.referral
      ? "Base amount per referral must be greater than 0."
      : "Maximum bonus must be greater than 0.";
  }
  if (meta.percentOfDeposit && !positive(form.bonusPercentage)) {
    errors.bonusPercentage = "Percentage of deposit must be greater than 0.";
  }
  if (meta.referral) {
    if (form.referralAmountMode === "PERCENT" && !positive(form.bonusPercentage)) {
      errors.bonusPercentage = "Percentage of the deposit must be greater than 0.";
    }
    // The backend requires every referral percentage to be non-null — the
    // wizard always posts them, defaulting to 0.
    if (pctInvalid(form.referrerPercentage)) {
      errors.referrerPercentage = "Enter 0–100 (0 = referrer gets nothing).";
    }
    if (pctInvalid(form.refereePercentage)) {
      errors.refereePercentage = "Enter 0–100 (0 = referee gets nothing).";
    }
    if (pctInvalid(form.recReferrerPercentage)) {
      errors.recReferrerPercentage = "Enter 0–100 (0 = no recurring reward).";
    }
  }

  if (effectiveReleaseMode(form) === "ROLLOVER") {
    // The DTO defaults rollOverMultiplier to 0 and the backend rejects 0 —
    // a rollover campaign must always carry a real multiplier.
    if (!positive(form.rollOverMultiplier)) {
      errors.rollOverMultiplier =
        "Rollover times must be at least 1 — the backend rejects rollover campaigns without it.";
    }
  } else {
    const instant = filled(form.instantBonus) ? num(form.instantBonus) : NaN;
    const wager = filled(form.wagerBonus) ? num(form.wagerBonus) : NaN;
    if (Number.isNaN(instant) || Number.isNaN(wager) || instant + wager !== 100) {
      errors.split = "Instant % and locked % must add up to 100.";
    } else if (meta.instantCap99 && wager < 1) {
      // DEPOSIT + WAGER grants ZERO when wagerBonus = 0 — force a locked part.
      errors.split =
        "For a wagered deposit match at least 1% must stay locked (instant % is capped at 99) — the engine grants nothing otherwise.";
    }
    if (!positive(form.wagerCriteria)) {
      errors.wagerCriteria = "Wager criteria % must be greater than 0.";
    }
    if (filled(form.maxInstantBonus) && !positive(form.maxInstantBonus)) {
      errors.maxInstantBonus =
        "Max instant bonus must be greater than 0 (leave empty to use the max bonus).";
    }
    if (filled(form.maxWinPercent) && Number(form.maxWinPercent) > 100) {
      errors.maxWinPercent = "Max winning % cannot be above 100.";
    }
  }

  // Bet-shape conditions are sports rules — a casino-scoped bonus has none
  // (the engine short-circuits odds and legs to true for GameType.CASINO).
  if (meta.betConditions && !casinoScoped(form)) {
    validateBetConditions(form, errors);
  }
  if (casinoScoped(form)) {
    const rowError = validateCasinoRows(form);
    if (rowError) errors.casinoRows = rowError;
  }

  // Free spins are optional here — only a started package has to be complete.
  if (meta.freeSpins && (form.freeSpinConfig || []).length > 0) {
    const rowError = validateFreeSpinRows(form);
    if (rowError) errors.freeSpinConfig = rowError;
  }
};

const validateReward = (form) => {
  const errors = {};
  const t = form.template;
  const meta = templateMeta(t);

  if (meta.kind === "FREEBET") {
    if (t === T.DEPOSIT_FREEBET) {
      // Deposit band lives here for this template (see validateQualify).
      Object.assign(errors, validateDepositBand(form));
      if (form.freeBetAmountMode === "FIXED") {
        if (!positive(form.freeBetFixedAmount)) {
          errors.freeBetFixedAmount =
            "Fixed free bet amount must be greater than 0.";
        }
      } else if (!positive(form.maxBonus)) {
        errors.maxBonus = "Maximum free bet must be greater than 0.";
      }
    } else if (!positive(form.maxBonus)) {
      errors.maxBonus = "Free bet amount must be greater than 0.";
    }
    // Min odds / min legs / odds scope — every free bet template sets
    // meta.betConditions, so this always runs for a FREEBET.
    validateBetConditions(form, errors);
    if (filled(form.maxWinPercent) && Number(form.maxWinPercent) > 100) {
      errors.maxWinPercent = "Max winning % cannot be above 100.";
    }
  } else if (meta.kind === "MONEY" && isSpinsOnly(form)) {
    // Dedicated free-spin campaign (INT-10950): the spin package IS the
    // reward, so it has to be complete, and none of the money rules apply.
    // The deposit band that gates the spins is validated on the Qualify step.
    const rowError = validateFreeSpinRows(form);
    if (rowError) errors.freeSpinConfig = rowError;
  } else if (meta.kind === "MONEY") {
    validateMoneyReward(form, errors);
  } else if (meta.kind === "SPINS") {
    const rowError = validateFreeSpinRows(form);
    if (rowError) errors.freeSpinConfig = rowError;
    if (meta.depositGate) {
      // The deposit IS the spin trigger — the backend gates allocation on it.
      Object.assign(errors, validateDepositBand(form));
    }
  } else if (meta.kind === "CASHBACK") {
    if (!positive(form.cashBackPercentage) || Number(form.cashBackPercentage) > 100) {
      errors.cashBackPercentage = "Cashback % must be between 1 and 100.";
    }
    if (!positive(form.maxCashBackAmount)) {
      errors.maxCashBackAmount =
        "The cashback pot is required — without it the campaign has no budget cap.";
    }
    if (meta.cashBackType === "BONUS_CASH") {
      const instant = filled(form.instantBonus) ? num(form.instantBonus) : NaN;
      const wager = filled(form.wagerBonus) ? num(form.wagerBonus) : NaN;
      if (Number.isNaN(instant) || Number.isNaN(wager) || instant + wager !== 100) {
        errors.split = "Instant % and locked % must add up to 100.";
      }
      if (!positive(form.wagerCriteria)) {
        errors.wagerCriteria = "Wager criteria % must be greater than 0.";
      }
    }
  } else if (meta.kind === "INSURANCE") {
    if (
      !positive(form.bonusPayoutPercentage) ||
      Number(form.bonusPayoutPercentage) > 100
    ) {
      errors.bonusPayoutPercentage = "Payout % of the stake must be 1–100.";
    }
  }

  if (!filled(form.expiryValue) || Number(form.expiryValue) < 1) {
    errors.expiryValue = "Validity must be at least 1.";
  }
  return errors;
};

const validateLimits = (form) => {
  const errors = {};
  const meta = templateMeta(form.template);
  // Auto free bets have no other dedupe — every qualifying event grants again
  // unless capped, so the cap is required (default 1), not optional.
  if (meta.grantCapRequired) {
    if (!filled(form.maxGrantsPerPlayer) || Number(form.maxGrantsPerPlayer) < 1) {
      errors.maxGrantsPerPlayer =
        "Required for automatic free bets — without it every qualifying event grants again.";
    }
  } else if (
    filled(form.maxGrantsPerPlayer) &&
    Number(form.maxGrantsPerPlayer) < 1
  ) {
    errors.maxGrantsPerPlayer = "Must be at least 1 (or leave empty for no cap).";
  }
  if (
    filled(form.maxGrantsPerPlayerPerDay) &&
    Number(form.maxGrantsPerPlayerPerDay) < 1
  ) {
    errors.maxGrantsPerPlayerPerDay =
      "Must be at least 1 (or leave empty for no cap).";
  }
  if (form.template === T.SCHEDULED_FREEBET) {
    if (filled(form.maxCategoryGrants) && Number(form.maxCategoryGrants) < 1) {
      errors.maxCategoryGrants = "Must be at least 1.";
    }
    if (filled(form.categoryWindowDays) && Number(form.categoryWindowDays) < 1) {
      errors.categoryWindowDays = "Must be at least 1 day.";
    }
  }
  return errors;
};

/** Returns { errors } for the given wizard step (0-indexed). */
export const validateStep = (step, form) => {
  if (step === 0) return form.template ? {} : { template: "Pick a campaign type." };
  if (step === 1) return validateQualify(form);
  if (step === 2) return validateReward(form);
  if (step === 3) return validateLimits(form);
  return {};
};

export const isStepValid = (step, form) =>
  Object.keys(validateStep(step, form)).length === 0;

// ─── Payload builder ─────────────────────────────────────────────────────────

const toLocalTime = (time) => (time && time.length === 5 ? `${time}:00` : time);

/** Market names → numeric applicableMarketIds (non-numeric ids are skipped). */
const toMarketIds = (markets = []) => {
  const ids = [];
  markets.forEach((m) => {
    const id = Number(m.marketId);
    if (Number.isNaN(id)) {
      console.warn(
        `Create Bonus V2: skipping non-numeric market id "${m.marketId}" (${m.marketName})`
      );
    } else {
      ids.push(id);
    }
  });
  return ids;
};

/** Scope row → legacy bonusSportSelection entry (falsy fields omitted). */
const toSportSelectionRow = (row) =>
  Object.keys(row).reduce((acc, key) => {
    if (row[key]) acc[key] = row[key];
    return acc;
  }, {});

/** The tags[] the template posts (fixed, or driven by the deposit tag). */
export const tagsFor = (form) => {
  const meta = templateMeta(form.template);
  if (form.template === T.BET_INSURANCE) {
    const cut = INSURANCE_CUT_OPTIONS.find((o) => o.value === form.insuranceCut);
    return [cut ? cut.tag : "one_cut"];
  }
  if (meta.depositTagSelect || meta.scheduledTagSelect) {
    return form.depositTag ? [form.depositTag] : [];
  }
  return meta.fixedTags || [];
};

/** freeSpinConfig rows in the exact backend shape (per-row min-deposit and
 * max-win intentionally absent — the backend overrides them from the body). */
const toFreeSpinConfig = (rows = []) =>
  rows.map((row) => ({
    providerName: row.providerName,
    subProviderName: row.subProviderName,
    gameName: row.gameName,
    ...(row.gameId != null && row.gameId !== "" && { gameId: row.gameId }),
    gameCode: row.gameCode || "",
    freeSpinAmount: Number(row.freeSpinAmount),
    numberOfFreeSpins: Number(row.numberOfFreeSpins),
  }));

/**
 * Builds the POST /bonus/create payload for the selected template.
 * @param {object} form - wizard form state
 * @param {number[]} defaultSports - all sport ids (used when no subset picked)
 */
export const buildV2Payload = (form, defaultSports = []) => {
  const t = form.template;
  const meta = templateMeta(t);
  const kind = meta.kind;
  const auto = meta.mode === "AUTO";
  const scheduled = Boolean(meta.scheduled);
  const rollover = kind === "MONEY" && effectiveReleaseMode(form) === "ROLLOVER";
  const scopeRows = form.scopeRows || [];
  const marketIds = toMarketIds(form.markets);
  const scopeSports = [...new Set(scopeRows.map((row) => Number(row.sportId)))];
  const scoped = scopeRows.length > 0 || marketIds.length > 0;
  const odds = form.oddsThreshold || "1.01";
  // A dedicated free-spin campaign on a wallet bonus type (INT-10950) posts
  // the spin rows and nothing else — no maxBonus, no split, no release rules.
  const spinsOnly = isSpinsOnly(form);
  // Games & Casino scope (INT-10950) — MONEY templates only, never together
  // with a spins-only reward (the spin rows already name the games).
  const casinoScope = casinoScoped(form);
  const casino = casinoScopeKeys(casinoScope ? form.casinoRows || [] : []);
  // Optional bet-shape conditions (INT-10926 / INT-10938). Both are opt-in:
  // a blank leg count means "no restriction" and SELECTION is the engine's
  // own default, so neither key is posted unless the operator sets it. They
  // are sports rules, so a casino-scoped bonus posts neither.
  const cumulativeOdds =
    Boolean(meta.betConditions) &&
    !casinoScope &&
    form.oddsThresholdScope === "CUMULATIVE";
  const minLegs =
    meta.betConditions && !casinoScope && form.numberOfLegs
      ? form.numberOfLegs
      : "";
  // Optional free spin package riding along with a wallet bonus (INT-10920).
  // Never reachable on a FREE_BET template — see the header note.
  const spinRows = meta.freeSpins ? form.freeSpinConfig || [] : [];

  const payload = {
    bonusType: t === T.BET_INSURANCE ? form.insuranceCut : meta.bonusType,
    bonusName: form.bonusName.trim(),
    bonusDescription: form.bonusDescription.trim(),
    // DIRECT is dead backend code — the wizard only ever posts AUTO or MANUAL.
    optIn: meta.mode,
    // A campaign is only ever created active or inactive — a derived status
    // (EXPIRED) is a date effect, never something to post.
    bonusStatus: form.bonusStatus === "IN_ACTIVE" ? "IN_ACTIVE" : "ACTIVE",
    startDate: convertStartDatePickerTimeToMySQLTime(form.startDate, "bonus"),
    validUntil: convertEndDatePickerTimeToMySQLTime(form.validUntil),
    // Expiry: days go to applyExpiry, hours to applyExpiryHours (never both).
    applyExpiry: form.expiryUnit === "HOURS" ? "" : form.expiryValue,
    ...(form.expiryUnit === "HOURS" &&
      form.expiryValue && { applyExpiryHours: Number(form.expiryValue) }),
    // ALWAYS sent: omitting bonusSubType trips a createBonus guard bug that
    // skips the maxInstantBonus check and the campaign then grants ZERO.
    // The value drives nothing (spins come from freeSpinConfig rows) but a
    // wallet bonus carrying spins declares both so the list/reports read right.
    bonusSubType:
      kind === "SPINS" || spinsOnly
        ? ["FREE_SPIN"]
        : spinRows.length > 0
          ? ["BONUS", "FREE_SPIN"]
          : ["BONUS"],
    tags: tagsFor(form),
    ...(form.currencyCode && { currencyCode: form.currencyCode }),
  };

  // Where the bonus applies.
  if (kind === "SPINS" || spinsOnly) {
    // Spins are casino allocations — no sports scope is posted. The games
    // themselves come from the freeSpinConfig rows.
    payload.applicableType = "CASINO";
  } else if (kind === "CASHBACK") {
    payload.applicableType = form.applicableTypeChoice;
    if (cashbackSportsFacing(form)) {
      payload.applicableSports = defaultSports;
    }
  } else if (kind === "INSURANCE") {
    payload.applicableType = "SPORTS";
    payload.applicableSports = defaultSports;
  } else if (casinoScope) {
    // Games & Casino: getUserBonus() matches applicable_type against the
    // bet's game type (or ALL), then placeBetWithBonus checks the bet's
    // provider/game against these lists. Both lists go out together or not
    // at all — see casinoScopeKeys().
    payload.applicableType = "CASINO";
    if (casino.providerName.length > 0) {
      payload.providerName = casino.providerName;
      payload.gameName = casino.gameName;
      if (casino.gameCode.length > 0) payload.gameCode = casino.gameCode;
    }
  } else {
    // FREEBET and MONEY: "SPORTS" when narrowed (scope rows or market ids),
    // otherwise "ALL" — both carry the sport id list, like the old page.
    payload.applicableType = scoped ? "SPORTS" : "ALL";
    payload.applicableSports = scopeRows.length > 0 ? scopeSports : defaultSports;
    if (scopeRows.length > 0) {
      payload.bonusSportSelection = scopeRows.map(toSportSelectionRow);
    }
    if (marketIds.length > 0) payload.applicableMarketIds = marketIds;
  }

  // Deposit band + which deposit qualifies.
  if (meta.depositBand || meta.depositGate || meta.referral) {
    payload.minDeposit = form.minDeposit;
    if (form.maxDeposit) payload.maxDeposit = Number(form.maxDeposit);
  }

  // ── Reward per kind ────────────────────────────────────────────────────────
  if (kind === "FREEBET") {
    if (t === T.DEPOSIT_FREEBET) {
      if (form.freeBetAmountMode === "FIXED") {
        // Fixed amount: the engine grants exactly freeBetFixedAmount when set,
        // so bonusPercentage is omitted; maxBonus mirrors it as a harmless cap.
        payload.freeBetFixedAmount = Number(form.freeBetFixedAmount);
        payload.maxBonus = form.freeBetFixedAmount;
      } else {
        // Match the deposit 1:1, capped by maxBonus.
        payload.bonusPercentage = 100;
        payload.maxBonus = form.maxBonus;
      }
    } else {
      // No deposit to match — the single amount is both the cap and the exact
      // fixed grant the engine pays out.
      payload.maxBonus = form.maxBonus;
      payload.freeBetFixedAmount = Number(form.maxBonus);
    }
    // Free bets are fully unlocked on grant.
    payload.instantBonus = 100;
    payload.wagerBonus = 0;
    payload.bonusCriteria = "WAGER_CRITERIA";
    if (form.oddsThreshold) payload.oddsThreshold = form.oddsThreshold;
    // Minimum odds read against the whole bet instead of each selection.
    if (cumulativeOdds) payload.isOddsThresholdCumulative = true;
    // Minimum legs a bet funded by this bonus must carry.
    if (minLegs) payload.numberOfLegs = minLegs;
    if (form.maxWinPercent) payload.maxWinPercentage = form.maxWinPercent;
    if (form.maxWinAmount) payload.maxWinAmount = form.maxWinAmount;
    if (form.stakeReturned) payload.stakeReturned = true;
  } else if (kind === "MONEY" && spinsOnly) {
    // Dedicated free-spin campaign on a wallet bonus type (INT-10950):
    // GOODWILL_FREE_SPINS / SIGNUP_CODE_FREE_SPINS /
    // RELOAD_CODE_FREE_SPINS / REFERRAL_FREE_SPINS. Everything that describes
    // a wallet bonus is dropped: with no amount, BonusDao.allocate() writes
    // no user_bonus_details row and still fires the "FREE SPINS EVENT" at the
    // end of the same block, and FreeSpinService creates the single
    // zero-amount row the spin rows hang off.
    if (meta.referral) {
      // createBonus rejects a REFERRAL campaign whose percentage fields are
      // literally null (isValidReferralBonus), so they still go out — they
      // simply have no amount to apply to.
      payload.referrerPercentage = String(form.referrerPercentage || "0");
      payload.refereePercentage = String(form.refereePercentage || "0");
      payload.recReferrerPercentage = String(form.recReferrerPercentage || "0");
    }
    if (meta.paymentMethods && form.paymentMethods.length > 0) {
      // Still honoured: the coupon match checks the payment method before the
      // grant path is even entered.
      payload.paymentMethods = form.paymentMethods;
    }
    payload.freeSpinConfig = toFreeSpinConfig(spinRows);
    if (form.maxWinAmount) payload.maxWinAmount = form.maxWinAmount;
    payload.bonusCriteria = "WAGER_CRITERIA";
  } else if (kind === "MONEY") {
    payload.maxBonus = form.maxBonus;
    if (meta.percentOfDeposit) payload.bonusPercentage = form.bonusPercentage;
    if (meta.referral) {
      if (form.referralAmountMode === "PERCENT") {
        payload.bonusPercentage = form.bonusPercentage;
      }
      // Backend requires every referral percentage non-null — post them all,
      // as strings so an explicit 0 survives payload cleaning.
      payload.referrerPercentage = String(form.referrerPercentage || "0");
      payload.refereePercentage = String(form.refereePercentage || "0");
      payload.recReferrerPercentage = String(form.recReferrerPercentage || "0");
      if (form.maxTotalBonus) payload.maxTotalBonus = form.maxTotalBonus;
    }
    if (meta.minStake && form.minStake) payload.minStake = form.minStake;
    payload.oddsThreshold = odds;
    // Minimum odds read against the whole bet instead of each selection.
    if (cumulativeOdds) payload.isOddsThresholdCumulative = true;
    // Minimum legs a bet funded by this bonus must carry.
    if (minLegs) payload.numberOfLegs = minLegs;
    // Which play releases the locked part. The unlock twins mirror the spend
    // scope: BonusDao's fallback (unlockProviderName ?: providerName) never
    // fires, because BonusDetailMapper does not read provider_name/game_name
    // at all — so a casino restriction that is not repeated here would leave
    // the locked part releasable by any play.
    payload.unlockApplicableType = casinoScope ? "CASINO" : "ALL";
    payload.unlockOddsThreshold = form.unlockOddsThreshold || odds;
    payload.unlockApplicableSports = defaultSports;
    if (casinoScope && casino.providerName.length > 0) {
      payload.unlockProviderName = casino.providerName;
      payload.unlockGameName = casino.gameName;
      if (casino.gameCode.length > 0) payload.unlockGameCode = casino.gameCode;
    }
    if (meta.unlockFields && form.unlockNumberOfLegs) {
      payload.unlockNumberOfLegs = form.unlockNumberOfLegs;
    }
    if (rollover) {
      // Rollover: the whole bonus stays locked until the multiplier is met.
      // The backend zeroes max-win fields on this path, so none are posted.
      payload.bonusCriteria = "ROLL_OVER_CRITERIA";
      payload.rollOverMultiplier = Number(form.rollOverMultiplier);
      payload.instantBonus = "0";
      payload.wagerBonus = "100";
    } else {
      payload.instantBonus = form.instantBonus;
      payload.wagerBonus = form.wagerBonus;
      payload.wagerCriteria = form.wagerCriteria;
      payload.bonusCriteria = "WAGER_CRITERIA";
      payload.rollOverMultiplier = 0;
      // Mandatory on the deposit/coupon WAGER path — the engine grants zero
      // without it. Auto-fill with the overall cap when left blank.
      payload.maxInstantBonus = Number(form.maxInstantBonus || form.maxBonus);
      if (form.maxWinPercent) payload.maxWinPercentage = form.maxWinPercent;
      if (form.maxWinAmount) payload.maxWinAmount = form.maxWinAmount;
    }
    if (meta.paymentMethods && form.paymentMethods.length > 0) {
      payload.paymentMethods = form.paymentMethods;
    }
    // Optional spin package. The backend rewrites every row's
    // freeSpinMinDepositAmount from body.minDeposit and freeSpinMaxWinAmount
    // from body.maxWinAmount, so only the shared campaign-level fields matter.
    if (spinRows.length > 0) {
      payload.freeSpinConfig = toFreeSpinConfig(spinRows);
    }
  } else if (kind === "SPINS") {
    // Per-row min-deposit / max-win are overridden by the backend from the
    // body — only the shared fields are posted.
    payload.freeSpinConfig = toFreeSpinConfig(form.freeSpinConfig);
    if (form.maxWinAmount) payload.maxWinAmount = form.maxWinAmount;
    payload.bonusCriteria = "WAGER_CRITERIA";
  } else if (kind === "CASHBACK") {
    payload.cashBackType = meta.cashBackType;
    // PER_BET is the only implemented criteria; CUMULATIVE is a dead stub and
    // cashBackFrequency is never read by the backend.
    payload.cashBackCriteria = "PER_BET";
    payload.cashBackBetStatus = form.cashBackBetStatus;
    payload.cashBackPercentage = form.cashBackPercentage;
    payload.maxCashBackAmount = form.maxCashBackAmount;
    payload.minStakeAmount = form.minStakeAmount;
    if (cashbackSportsFacing(form)) {
      payload.minCashBackOdds = form.minCashBackOdds;
      payload.minCashBackLegs = form.minCashBackLegs;
      if (form.cashBackBetType.length > 0) {
        payload.cashBackBetType = form.cashBackBetType;
      }
    }
    payload.bonusCriteria = "WAGER_CRITERIA";
    if (meta.cashBackType === "REAL_CASH") {
      if (form.addToWithdrawableAmount) payload.addToWithdrawableAmount = true;
    } else {
      // Bonus cash lands in the bonus wallet through the usual split.
      payload.instantBonus = form.instantBonus;
      payload.wagerBonus = form.wagerBonus;
      payload.wagerCriteria = form.wagerCriteria;
    }
  } else if (kind === "INSURANCE") {
    payload.oddsThreshold = form.oddsThreshold;
    payload.numberOfLegs = form.numberOfLegs;
    payload.minStake = form.minStake;
    payload.bonusPayoutPercentage = form.bonusPayoutPercentage;
    payload.bonusCriteria = "WAGER_CRITERIA";
  }

  // Codes are only ever posted for MANUAL templates — the backend force-nulls
  // bonusCode on AUTO campaigns.
  if (meta.code) {
    payload.bonusCode = form.bonusCode.trim();
    if (form.allowTags.length > 0) payload.allowTags = form.allowTags;
    if (form.denyTags.length > 0) payload.denyTags = form.denyTags;
  }

  if (auto) {
    payload.triggerMode = scheduled ? "SCHEDULED" : "EVENT";
    if (form.campaignDays.length > 0) payload.campaignDays = form.campaignDays;
    if (form.timeFrom) payload.timeFrom = toLocalTime(form.timeFrom);
    if (form.timeTo) payload.timeTo = toLocalTime(form.timeTo);
  }

  if (scheduled) {
    if (form.inactiveDaysMin) payload.inactiveDaysMin = Number(form.inactiveDaysMin);
    if (form.depositWindowDays)
      payload.depositWindowDays = Number(form.depositWindowDays);
    if (form.depositWindowMinAmount)
      payload.depositWindowMinAmount = Number(form.depositWindowMinAmount);
    if (form.requireLifetimeDeposit) payload.requireLifetimeDeposit = true;
    if (form.casinoLossPercentMin)
      payload.casinoLossPercentMin = Number(form.casinoLossPercentMin);
    if (form.casinoWindowDays)
      payload.casinoWindowDays = Number(form.casinoWindowDays);
    if (form.campaignCategory && form.campaignCategory !== "NONE") {
      payload.campaignCategory = form.campaignCategory;
      if (form.maxCategoryGrants)
        payload.maxCategoryGrants = Number(form.maxCategoryGrants);
      if (form.categoryWindowDays)
        payload.categoryWindowDays = Number(form.categoryWindowDays);
    }
  }

  if (form.maxGrantsPerPlayer)
    payload.maxGrantsPerPlayer = Number(form.maxGrantsPerPlayer);
  if (form.maxGrantsPerPlayerPerDay)
    payload.maxGrantsPerPlayerPerDay = Number(form.maxGrantsPerPlayerPerDay);

  return payload;
};

// ─── Edit: change in place, or replace the campaign ──────────────────────────
// PUT /bonus/update accepts nothing but these two — every other change means a
// new campaign, because the engine cannot re-cut the terms of a live bonus.

export const SAFE_EDIT_KEYS = ["bonusStatus", "validUntil"];

const sameValue = (a, b) =>
  a === b ||
  JSON.stringify(a === undefined ? null : a) ===
    JSON.stringify(b === undefined ? null : b);

// A start date that falls on today is stamped with the current clock time by
// convertStartDatePickerTimeToMySQLTime(), so two builds of the very same date
// are seconds apart — compare the calendar day only.
const DAY_ONLY_KEYS = ["startDate"];

const comparable = (key, value) =>
  DAY_ONLY_KEYS.includes(key) && typeof value === "string"
    ? value.slice(0, 10)
    : value;

/**
 * Payload keys that differ between the original campaign and the edited one,
 * ignoring the keys the update endpoint can change in place. Both payloads
 * come out of buildV2Payload(), so key order and shapes line up.
 */
export const criteriaDiff = (basePayload = {}, nextPayload = {}) =>
  [...new Set([...Object.keys(basePayload), ...Object.keys(nextPayload)])]
    .filter(
      (key) =>
        !SAFE_EDIT_KEYS.includes(key) &&
        !sameValue(
          comparable(key, basePayload[key]),
          comparable(key, nextPayload[key])
        )
    )
    .sort();

const PAYLOAD_LABELS = {
  addToWithdrawableAmount: "Add to withdrawable",
  applicableMarketIds: "Markets",
  applicableSports: "Sports",
  applicableType: "Applies to",
  applyExpiry: "Bonus validity (days)",
  applyExpiryHours: "Bonus validity (hours)",
  bonusCode: "Bonus code",
  bonusCriteria: "Release criteria",
  bonusDescription: "Description",
  bonusName: "Campaign name",
  bonusPayoutPercentage: "Payout % of stake",
  bonusPercentage: "Bonus %",
  bonusSportSelection: "Scope selection",
  bonusSubType: "Bonus sub-type",
  bonusType: "Bonus type",
  campaignDays: "Active days",
  cashBackBetStatus: "Cashback on",
  cashBackBetType: "Qualifying bet types",
  cashBackCriteria: "Cashback criteria",
  cashBackPercentage: "Cashback %",
  cashBackType: "Cashback paid as",
  casinoLossPercentMin: "Casino loss %",
  casinoWindowDays: "Casino window",
  categoryWindowDays: "Category window",
  currencyCode: "Currency",
  depositWindowDays: "Deposit window",
  depositWindowMinAmount: "Deposit window amount",
  freeBetFixedAmount: "Fixed free bet amount",
  freeSpinConfig: "Free spin games",
  gameCode: "Casino game codes",
  gameName: "Casino games",
  inactiveDaysMin: "Inactive days",
  instantBonus: "Instant %",
  isOddsThresholdCumulative: "Minimum odds apply to",
  maxBonus: "Maximum bonus",
  maxCashBackAmount: "Cashback pot",
  maxCategoryGrants: "Category grant cap",
  maxDeposit: "Maximum deposit",
  maxGrantsPerPlayer: "Grants per player",
  maxGrantsPerPlayerPerDay: "Grants per player per day",
  maxInstantBonus: "Max instant bonus",
  maxTotalBonus: "Referrer lifetime cap",
  maxWinAmount: "Max win amount",
  maxWinPercentage: "Max win %",
  minCashBackLegs: "Min legs",
  minCashBackOdds: "Min odds",
  minDeposit: "Minimum deposit",
  minStake: "Minimum stake",
  minStakeAmount: "Minimum stake",
  numberOfLegs: "Minimum selections (legs)",
  oddsThreshold: "Minimum odds",
  optIn: "Opt-in",
  paymentMethods: "Payment methods",
  providerName: "Casino providers",
  recReferrerPercentage: "Recurring referrer %",
  refereePercentage: "Referee %",
  referrerPercentage: "Referrer %",
  rollOverMultiplier: "Rollover times",
  startDate: "Start date",
  stakeReturned: "Stake returned",
  tags: "Campaign tag",
  timeFrom: "Active from",
  timeTo: "Active until",
  triggerMode: "Trigger mode",
  unlockApplicableSports: "Release sports",
  unlockApplicableType: "Release scope",
  unlockGameCode: "Release game codes",
  unlockGameName: "Release casino games",
  unlockNumberOfLegs: "Release min legs",
  unlockOddsThreshold: "Release min odds",
  unlockProviderName: "Release casino providers",
  wagerBonus: "Locked %",
  wagerCriteria: "Wager criteria %",
};

/** "maxWinAmount" → "Max win amount" (falls back to the split camelCase). */
export const payloadFieldLabel = (key) =>
  PAYLOAD_LABELS[key] ||
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

// ─── Human-readable summary ──────────────────────────────────────────────────

const money = (v, ccy) => (filled(v) ? `${ccy || ""}${ccy ? " " : ""}${v}` : "");
const tagLabel = (tag) =>
  (SCHEDULED_TAG_OPTIONS.find((o) => o.value === tag) || {}).label || tag;

/** "Football › England › Premier League › Arsenal vs Chelsea" */
export const scopeRowLabel = (row) =>
  [row.sportName, row.countryName, row.leagueName, row.eventName]
    .filter(Boolean)
    .join(" › ");

/** "Pragmatic › PragmaticPlay › Gates of Olympus" */
export const casinoRowLabel = (row) =>
  [row.providerName, row.subProviderName, row.gameName]
    .filter(Boolean)
    .join(" › ");

const releaseSentence = (form, ccy) =>
  effectiveReleaseMode(form) === "ROLLOVER"
    ? `fully locked until the amount is rolled over ${form.rollOverMultiplier || "?"}×`
    : `${form.instantBonus}% available instantly, ${form.wagerBonus}% released ` +
      `after wagering ${form.wagerCriteria}% turnover` +
      (form.maxInstantBonus
        ? ` (instant part capped at ${money(form.maxInstantBonus, ccy)})`
        : "");

const spinsSentence = (form, ccy) => {
  const rows = form.freeSpinConfig || [];
  const totalSpins = rows.reduce(
    (sum, row) => sum + (Number(row.numberOfFreeSpins) || 0),
    0
  );
  const games = rows
    .filter((row) => row.gameName)
    .map((row) => row.gameName)
    .join(", ");
  return `${totalSpins || "?"} free spin(s)${
    rows[0] && rows[0].freeSpinAmount
      ? ` (${rows.map((r) => money(r.freeSpinAmount, ccy)).join(" / ")} per spin)`
      : ""
  } on ${games || "the configured game(s)"}`;
};

export const buildSummary = (form) => {
  const t = form.template;
  const ccy = form.currencyCode;
  const lines = [];
  const scopeRows = form.scopeRows || [];
  const marketNames = (form.markets || []).map((m) => m.marketName);
  const expiry = `${form.expiryValue} ${
    form.expiryUnit === "HOURS" ? "hour(s)" : "day(s)"
  } after grant`;
  const sportsScope =
    scopeRows.length > 0
      ? `on ${[...new Set(scopeRows.map((row) => row.sportName))].join(", ")}`
      : "on any sport";
  const odds = form.oddsThreshold ? ` at odds ${form.oddsThreshold}+` : "";
  const markets =
    marketNames.length > 0
      ? `, restricted to the ${marketNames.join(", ")} market(s)`
      : "";
  const band = form.maxDeposit
    ? `between ${money(form.minDeposit, ccy)} and ${money(form.maxDeposit, ccy)}`
    : `of at least ${money(form.minDeposit, ccy)}`;

  if (isSpinsOnly(form)) {
    // Dedicated free-spin campaign on a wallet bonus type (INT-10950) — no
    // bonus money at all.
    const trigger =
      t === T.REFERRAL_FREE_SPINS
        ? `When a referred player deposits at least ${money(form.minDeposit, ccy)}, the referrer receives`
        : t === T.RELOAD_CODE_FREE_SPINS
          ? `Players redeeming code ${form.bonusCode || "(not set)"} with a deposit ${band} receive`
          : t === T.SIGNUP_CODE_FREE_SPINS
            ? `Players entering code ${form.bonusCode || "(not set)"} at registration receive`
            : t === T.ENGAGEMENT_FREE_SPINS
              ? `Engagement code ${form.bonusCode || "(not set)"} grants`
              : `Goodwill code ${form.bonusCode || "(not set)"} grants`;
    lines.push(
      `${trigger} ${spinsSentence(form, ccy)} and NO bonus money — the ` +
        `campaign has no wallet component. Expires ${expiry}.` +
        (form.maxWinAmount
          ? ` Winnings from the spins are capped at ${money(form.maxWinAmount, ccy)}.`
          : "")
    );
    if (t === T.GOODWILL_FREE_SPINS || t === T.ENGAGEMENT_FREE_SPINS) {
      lines.push(
        "Grant it to specific players via Assign Bonus or bulk CSV — it is never granted automatically."
      );
    }
    if (t === T.RELOAD_CODE_FREE_SPINS) {
      lines.push(
        `Deposits below ${money(form.minDeposit, ccy)} allocate no spins — the engine applies the minimum deposit as the spin gate on deposit events.`
      );
    }
    if (t === T.SIGNUP_CODE_FREE_SPINS) {
      lines.push(
        "The code is redeemed at registration, not on a deposit, so no minimum deposit gates the spins."
      );
    }
    if (t === T.REFERRAL_FREE_SPINS) {
      lines.push(
        "The spins go to the REFERRER (the player whose code was used), not the depositor. A referral whose deposit is below the minimum is skipped before the spins are allocated."
      );
    }
  } else if (t === T.DEPOSIT_FREEBET) {
    const amount =
      form.freeBetAmountMode === "FIXED"
        ? `receive a fixed free bet of ${money(form.freeBetFixedAmount, ccy)}`
        : `receive a free bet matching the deposit, up to ` +
          `${money(form.maxBonus, ccy)}`;
    lines.push(
      `Players depositing ${band} (${tagLabel(form.depositTag).toLowerCase()}) ` +
        `${amount}, usable ${sportsScope}${odds}${markets}, expiring ${expiry}.`
    );
  } else if (t === T.FREEBET_DROP) {
    lines.push(
      `Bonus code ${form.bonusCode || "(not set)"} grants a free bet of ` +
        `${money(form.maxBonus, ccy)}, usable ${sportsScope}${odds}${markets}, expiring ${expiry}. ` +
        `Assign it to players from the Apply Bonus page.`
    );
  } else if (t === T.SCHEDULED_FREEBET) {
    const criteria = [];
    if (form.inactiveDaysMin)
      criteria.push(`inactive for ${form.inactiveDaysMin}+ days`);
    if (form.depositWindowDays || form.depositWindowMinAmount) {
      criteria.push(
        `deposited ${
          form.depositWindowMinAmount
            ? `at least ${money(form.depositWindowMinAmount, ccy)} `
            : ""
        }in the last ${form.depositWindowDays || "?"} days`
      );
    }
    if (form.requireLifetimeDeposit) criteria.push("have deposited before");
    if (form.casinoLossPercentMin || form.casinoWindowDays) {
      criteria.push(
        `lost ${form.casinoLossPercentMin || "?"}%+ in casino over the last ${
          form.casinoWindowDays || "?"
        } days`
      );
    }
    lines.push(
      `Every day, players who ${criteria.join(" and ") || "match the criteria"} ` +
        `automatically receive a free bet of ${money(form.maxBonus, ccy)}, ` +
        `usable ${sportsScope}${odds}${markets}, expiring ${expiry}.`
    );
    lines.push(
      `Campaign category: ${form.campaignCategory}` +
        (form.maxCategoryGrants
          ? ` — max ${form.maxCategoryGrants} grant(s) per player across this category every ${form.categoryWindowDays} days.`
          : ".")
    );
    lines.push(
      `The scheduler routes grants through the "${tagLabel(form.depositTag)}" tag.`
    );
  } else if (t === T.DEPOSIT_MATCH) {
    lines.push(
      `Players depositing ${band} (${tagLabel(form.depositTag).toLowerCase()}) ` +
        `receive ${form.bonusPercentage}% of the deposit as bonus money up to ` +
        `${money(form.maxBonus, ccy)} — ${releaseSentence(form, ccy)}, expiring ${expiry}.`
    );
  } else if (t === T.SIGNUP_BONUS) {
    lines.push(
      `Every new registration automatically receives ${money(form.maxBonus, ccy)} ` +
        `bonus money — ${releaseSentence(form, ccy)}, expiring ${expiry}.`
    );
    if (form.unlockOddsThreshold || form.unlockNumberOfLegs) {
      lines.push(
        `Releasing bets must have ${form.unlockNumberOfLegs || "any"} leg(s)` +
          (form.unlockOddsThreshold
            ? ` at odds ${form.unlockOddsThreshold}+`
            : "") +
          `.`
      );
    }
  } else if (t === T.SIGNUP_FREE_SPINS) {
    lines.push(
      `Every new registration automatically receives ${spinsSentence(form, ccy)}, ` +
        `expiring ${expiry}.` +
        (form.maxWinAmount
          ? ` Winnings from the spins are capped at ${money(form.maxWinAmount, ccy)}.`
          : "")
    );
  } else if (t === T.REFERRAL_REWARD) {
    const base =
      form.referralAmountMode === "PERCENT"
        ? `${form.bonusPercentage}% of the deposit (capped at ${money(form.maxBonus, ccy)})`
        : `${money(form.maxBonus, ccy)}`;
    lines.push(
      `When a referred player deposits at least ${money(form.minDeposit, ccy)}, ` +
        `a base amount of ${base} is split: referrer ${form.referrerPercentage}% ` +
        `(${form.recReferrerPercentage}% on further referrals), referee ${form.refereePercentage}% — ` +
        `${releaseSentence(form, ccy)}, expiring ${expiry}.`
    );
    if (form.maxTotalBonus) {
      lines.push(
        `A referrer can earn at most ${money(form.maxTotalBonus, ccy)} from this campaign, lifetime.`
      );
    }
  } else if (t === T.DEPOSIT_FREE_SPINS) {
    lines.push(
      `Players depositing ${band} (${tagLabel(form.depositTag).toLowerCase()}) ` +
        `receive ${spinsSentence(form, ccy)}, expiring ${expiry}.` +
        (form.maxWinAmount
          ? ` Winnings from the spins are capped at ${money(form.maxWinAmount, ccy)}.`
          : "")
    );
  } else if (t === T.RELOAD_CODE) {
    lines.push(
      `Players redeeming code ${form.bonusCode || "(not set)"} with a reload deposit ${band} ` +
        `receive ${form.bonusPercentage}% of the deposit as bonus money up to ${money(form.maxBonus, ccy)} — ` +
        `${releaseSentence(form, ccy)}, expiring ${expiry}. One use per player is enforced by the backend.`
    );
    if (form.paymentMethods.length > 0) {
      lines.push(
        `Only deposits made via ${form.paymentMethods.join(", ")} qualify.`
      );
    }
  } else if (t === T.SIGNUP_CODE) {
    lines.push(
      `Players entering code ${form.bonusCode || "(not set)"} at registration receive ` +
        `${money(form.maxBonus, ccy)} bonus money — ${releaseSentence(form, ccy)}, expiring ${expiry}.`
    );
  } else if (t === T.GOODWILL_CREDIT || t === T.ENGAGEMENT_CREDIT) {
    const label = t === T.ENGAGEMENT_CREDIT ? "Engagement" : "Goodwill";
    lines.push(
      `${label} code ${form.bonusCode || "(not set)"} credits ${money(form.maxBonus, ccy)} ` +
        `bonus money — ${releaseSentence(form, ccy)}, expiring ${expiry}. ` +
        `Grant it to specific players via Assign Bonus or bulk CSV — it is never granted automatically.`
    );
    if (form.minStake) {
      lines.push(
        `Bets using this bonus must stake at least ${money(form.minStake, ccy)}.`
      );
    }
  } else if (t === T.CASHBACK_REAL || t === T.CASHBACK_BONUS) {
    const as =
      t === T.CASHBACK_REAL
        ? form.addToWithdrawableAmount
          ? "withdrawable real cash"
          : "real cash"
        : "bonus money";
    const betShape = cashbackSportsFacing(form)
      ? ` on ${form.cashBackBetType.join("/") || "any"} bets with ${
          form.minCashBackLegs || "any"
        }+ leg(s) at odds ${form.minCashBackOdds || "any"}+`
      : "";
    lines.push(
      `${form.cashBackPercentage || "?"}% of every ${String(
        form.cashBackBetStatus || "lost"
      ).toLowerCase()} bet of ${money(form.minStakeAmount, ccy) || "any stake"}+` +
        `${betShape} is returned as ${as}, per bet, expiring ${expiry}.`
    );
    lines.push(
      `The campaign stops once the shared pot of ${money(form.maxCashBackAmount, ccy)} ` +
        `is exhausted — the pot is brand-wide, across ALL players.`
    );
    if (t === T.CASHBACK_BONUS) {
      lines.push(`Cashback lands ${releaseSentence(form, ccy)}.`);
    }
  } else if (t === T.BET_INSURANCE) {
    const cutLabel = form.insuranceCut === "TWOCUT" ? "two legs" : "one leg";
    lines.push(
      `Accumulators with ${form.numberOfLegs || "?"}+ legs, every leg at odds ` +
        `${form.oddsThreshold || "?"}+ and a stake of at least ${money(form.minStake, ccy)}, ` +
        `get ${form.bonusPayoutPercentage || "?"}% of the stake back if exactly ` +
        `${cutLabel} lose(s). Refund expires ${expiry}.`
    );
    lines.push(
      `Only one active ${form.insuranceCut === "TWOCUT" ? "two-cut" : "one-cut"} ` +
        `campaign can exist per brand — creating a second is rejected by the API.`
    );
  }

  const meta = templateMeta(t);
  if (meta.betConditions) {
    const conditions = [];
    if (form.numberOfLegs) {
      conditions.push(`at least ${form.numberOfLegs} selection(s)`);
    }
    if (form.oddsThreshold) {
      conditions.push(
        form.oddsThresholdScope === "CUMULATIVE"
          ? `total odds of ${form.oddsThreshold}+ across the whole bet`
          : `every selection at odds ${form.oddsThreshold}+`
      );
    }
    if (conditions.length > 0) {
      lines.push(`Qualifying bets must have ${conditions.join(" and ")}.`);
    }
  }
  if (meta.freeSpins && !isSpinsOnly(form) && (form.freeSpinConfig || []).length > 0) {
    lines.push(`The same grant also awards ${spinsSentence(form, ccy)}.`);
  }
  if (casinoScoped(form)) {
    const casinoRows = form.casinoRows || [];
    lines.push(
      casinoRows.length > 0
        ? `Usable on casino games only: ${casinoRows
          .map((row) => casinoRowLabel(row))
          .join("; ")}. The same games release the locked part.`
        : "Usable on casino games only — any game, any provider."
    );
  }
  if (scopeRows.length > 0 && !casinoScoped(form)) {
    lines.push(
      `Scope: ${scopeRows.map((row) => scopeRowLabel(row)).join("; ")}.`
    );
  }
  if (marketNames.length > 0 && meta.kind === "MONEY" && !casinoScoped(form)) {
    lines.push(`Restricted to the ${marketNames.join(", ")} market(s).`);
  }
  if (form.stakeReturned) {
    lines.push("Free bet winnings include the stake (stake returned).");
  }
  if (meta.kind === "FREEBET") {
    if (form.maxWinPercent) {
      lines.push(`Winnings are capped at ${form.maxWinPercent}% of the payout.`);
    }
    if (form.maxWinAmount) {
      lines.push(
        `Winnings from the bonus are capped at ${money(form.maxWinAmount, ccy)}.`
      );
    }
  }
  const caps = [];
  if (form.maxGrantsPerPlayer) caps.push(`max ${form.maxGrantsPerPlayer} grant(s) per player`);
  if (form.maxGrantsPerPlayerPerDay)
    caps.push(`max ${form.maxGrantsPerPlayerPerDay} per player per day`);
  if (caps.length) lines.push(`Safety limits: ${caps.join(", ")}.`);
  if (form.campaignDays.length > 0 || (form.timeFrom && form.timeTo)) {
    const days = form.campaignDays.length > 0 ? `on ${form.campaignDays.join(", ")}` : "every day";
    const window =
      form.timeFrom && form.timeTo ? ` between ${form.timeFrom} and ${form.timeTo}` : "";
    lines.push(`Active ${days}${window}.`);
  }
  return lines;
};

export const templateById = (id) => TEMPLATES.find((tpl) => tpl.id === id);

// ─── Edit / Duplicate: API bonus row → wizard form (best-effort reverse of
// buildV2Payload). Field names are the camelCase entity property names the
// backend serializes on GET /bonus/all (BonusDetail / BonusDetailsResponse). ──

const rowTags = (bonus) => (Array.isArray(bonus.tags) ? bonus.tags : []);
const rowSubTypes = (bonus) =>
  Array.isArray(bonus.bonusSubType) ? bonus.bonusSubType : [];

/**
 * Which of the 18 templates a live bonus row corresponds to, or null.
 *
 * `spinsOnly` is the discriminator the payload builder writes for the four
 * dedicated free spin campaigns that sit on a wallet bonus type: FREE_SPIN
 * declared WITHOUT BONUS. Campaigns created before those templates existed
 * (through the old reward-shape radio) carry exactly the same sub-types, so
 * they open on the new template and round-trip unchanged.
 */
export const detectTemplateForBonus = (bonus) => {
  if (!bonus) return null;
  const subTypes = rowSubTypes(bonus);
  const spins = subTypes.includes("FREE_SPIN");
  const spinsOnly = spins && !subTypes.includes("BONUS");
  switch (bonus.bonusType) {
    case "FREE_BET":
      if (bonus.optIn === "MANUAL") return T.FREEBET_DROP;
      if (bonus.optIn === "AUTO") {
        return bonus.triggerMode === "SCHEDULED"
          ? T.SCHEDULED_FREEBET
          : T.DEPOSIT_FREEBET;
      }
      return null;
    case "DEPOSIT":
      if (bonus.optIn !== "AUTO") return null;
      return spins ? T.DEPOSIT_FREE_SPINS : T.DEPOSIT_MATCH;
    case "REGISTER":
      if (bonus.optIn !== "AUTO") return null;
      return spins ? T.SIGNUP_FREE_SPINS : T.SIGNUP_BONUS;
    case "REFERRAL":
      return spinsOnly ? T.REFERRAL_FREE_SPINS : T.REFERRAL_REWARD;
    case "COUPONCODE": {
      // Legacy DIRECT coupon rows open here too — saving re-posts as MANUAL.
      if (bonus.optIn === "AUTO") return null;
      const atSignup = rowTags(bonus).includes("register_dir");
      if (spinsOnly) {
        return atSignup ? T.SIGNUP_CODE_FREE_SPINS : T.RELOAD_CODE_FREE_SPINS;
      }
      return atSignup ? T.SIGNUP_CODE : T.RELOAD_CODE;
    }
    case "GOODWILL":
      return spinsOnly ? T.GOODWILL_FREE_SPINS : T.GOODWILL_CREDIT;
    case "ENGAGEMENT":
      return spinsOnly ? T.ENGAGEMENT_FREE_SPINS : T.ENGAGEMENT_CREDIT;
    case "CASHBACK":
      return bonus.cashBackType === "BONUS_CASH" ? T.CASHBACK_BONUS : T.CASHBACK_REAL;
    case "ONECUT":
    case "TWOCUT":
      return T.BET_INSURANCE;
    default:
      return null;
  }
};

const toFormValue = (v) => (v === null || v === undefined || v === 0 ? "" : String(v));

// Percentages where an explicit 0 is meaningful (referral splits).
const toPctValue = (v) => (v === null || v === undefined || v === "" ? "0" : String(v));

// "20:00:00Z" / "20:00Z" -> "20:00" for <input type="time">
const toFormTime = (t) => (t ? String(t).replace("Z", "").slice(0, 5) : "");

// paymentMethods often come back as ["id1,id2"] — flatten to ["id1", "id2"].
const toPaymentList = (v) =>
  Array.isArray(v)
    ? v
      .flatMap((entry) => String(entry).split(","))
      .map((entry) => entry.trim())
      .filter(Boolean)
    : [];

/**
 * The casino scope lists come back the way the backend stored them: a
 * one-element array holding the comma-joined values ("A,B"), sometimes a bare
 * string. Flatten either shape back into the individual values.
 */
const toNameList = (v) => {
  const entries = Array.isArray(v) ? v : typeof v === "string" && v ? [v] : [];
  return entries
    .flatMap((entry) => String(entry).split(","))
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const toBetTypeList = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v) {
    return v.split(",").map((entry) => entry.trim()).filter(Boolean);
  }
  return [];
};

/**
 * Builds a prefilled wizard form from a bonus returned by GET /bonus/all.
 * Returns { template, form } or null when the bonus doesn't match any of the
 * supported templates. Best-effort: allow/deny tags come back from the API as
 * tag names (not ids) so they are not carried over, market ids are kept
 * without their display names, and freeSpinConfig rows are fetched separately
 * (GET /free-spin/{bonusId}/config) by the wizard.
 */
export const prefillFromBonus = (bonus) => {
  const template = detectTemplateForBonus(bonus);
  if (!template) return null;
  const meta = templateMeta(template);
  const form = formForTemplate(template);

  // Basics — the create payload stores the campaign name as bonusCode for
  // non-coupon bonuses, so the code doubles as the name.
  form.bonusName = bonus.bonusName || bonus.bonusCode || "";
  form.bonusDescription = bonus.bonusDescription || "";
  form.currencyCode = bonus.currencyCode || "";
  form.bonusStatus = bonus.bonusStatus || "ACTIVE";
  form.startDate = bonus.startDate ? new Date(bonus.startDate) : null;
  form.validUntil = bonus.validUntil ? new Date(bonus.validUntil) : null;

  // Qualifying
  const tags = rowTags(bonus);
  const knownTag = tags.find((tag) =>
    SCHEDULED_TAG_OPTIONS.some((opt) => opt.value === tag)
  );
  if (knownTag) form.depositTag = knownTag;
  form.minDeposit = toFormValue(bonus.minDeposit);
  form.maxDeposit = toFormValue(bonus.maxDeposit);
  if (meta.code) {
    form.bonusCode = bonus.bonusCode || "";
    form.paymentMethods = toPaymentList(bonus.paymentMethods);
  }
  form.inactiveDaysMin = toFormValue(bonus.inactiveDaysMin);
  form.depositWindowDays = toFormValue(bonus.depositWindowDays);
  form.depositWindowMinAmount = toFormValue(bonus.depositWindowMinAmount);
  form.requireLifetimeDeposit = Boolean(bonus.requireLifetimeDeposit);
  form.casinoLossPercentMin = toFormValue(bonus.casinoLossPercentMin);
  form.casinoWindowDays = toFormValue(bonus.casinoWindowDays);
  if (bonus.campaignCategory) form.campaignCategory = bonus.campaignCategory;
  form.campaignDays = Array.isArray(bonus.campaignDays) ? bonus.campaignDays : [];
  form.timeFrom = toFormTime(bonus.campaignTimeFrom);
  form.timeTo = toFormTime(bonus.campaignTimeTo);

  // Reward
  form.maxBonus = toFormValue(bonus.maxBonus);
  if (template === T.DEPOSIT_FREEBET && bonus.freeBetFixedAmount) {
    form.freeBetAmountMode = "FIXED";
    form.freeBetFixedAmount = String(bonus.freeBetFixedAmount);
  }

  // Wallet fields. A spins-only campaign has none of them — only the referral
  // percentages survive, because createBonus rejects a REFERRAL campaign whose
  // percentages are literally null, so the payload still carries them.
  if (meta.kind === "MONEY" && meta.spinsOnly) {
    if (meta.referral) {
      form.referrerPercentage = toPctValue(bonus.referrerPercentage);
      form.refereePercentage = toPctValue(bonus.refereePercentage);
      form.recReferrerPercentage = toPctValue(bonus.recReferrerPercentage);
    }
  } else if (meta.kind === "MONEY") {
    if (meta.percentOfDeposit) {
      form.bonusPercentage = toFormValue(bonus.bonusPercentage) || "100";
    }
    if (meta.rolloverAllowed && bonus.bonusCriteria === "ROLL_OVER_CRITERIA") {
      form.releaseMode = "ROLLOVER";
      form.rollOverMultiplier = toFormValue(bonus.rollOverMultiplier);
    } else {
      form.instantBonus = filled(bonus.instantBonus) ? String(bonus.instantBonus) : "0";
      form.wagerBonus = filled(bonus.wagerBonus) ? String(bonus.wagerBonus) : "100";
      form.wagerCriteria = toFormValue(bonus.wagerCriteria) || "20";
      // Only surface an explicit cap — when it equals maxBonus the wizard
      // regenerates it automatically anyway.
      if (
        filled(bonus.maxInstantBonus) &&
        Number(bonus.maxInstantBonus) !== Number(bonus.maxBonus)
      ) {
        form.maxInstantBonus = String(bonus.maxInstantBonus);
      }
    }
    if (meta.unlockFields) {
      if (bonus.unlockOddsThreshold) {
        form.unlockOddsThreshold = String(bonus.unlockOddsThreshold);
      }
      form.unlockNumberOfLegs = toFormValue(bonus.unlockNumberOfLegs);
    }
    if (meta.referral) {
      form.referralAmountMode = positive(bonus.bonusPercentage) ? "PERCENT" : "FIXED";
      if (positive(bonus.bonusPercentage)) {
        form.bonusPercentage = String(bonus.bonusPercentage);
      }
      form.referrerPercentage = toPctValue(bonus.referrerPercentage);
      form.refereePercentage = toPctValue(bonus.refereePercentage);
      form.recReferrerPercentage = toPctValue(bonus.recReferrerPercentage);
      form.maxTotalBonus = toFormValue(bonus.maxTotalBonus);
    }
    if (meta.minStake) form.minStake = toFormValue(bonus.minStake);
  }

  if (meta.kind === "CASHBACK") {
    if (bonus.applicableType) form.applicableTypeChoice = bonus.applicableType;
    if (bonus.cashBackBetStatus) form.cashBackBetStatus = bonus.cashBackBetStatus;
    form.cashBackPercentage = toFormValue(bonus.cashBackPercentage);
    form.maxCashBackAmount = toFormValue(bonus.maxCashBackAmount);
    form.minStakeAmount = toFormValue(bonus.minStakeAmount);
    form.minCashBackOdds = toFormValue(bonus.minCashBackOdds);
    form.minCashBackLegs = toFormValue(bonus.minCashBackLegs);
    form.cashBackBetType = toBetTypeList(bonus.cashBackBetType);
    form.addToWithdrawableAmount = Boolean(bonus.addToWithdrawableAmount);
    if (meta.cashBackType === "BONUS_CASH") {
      form.instantBonus = filled(bonus.instantBonus) ? String(bonus.instantBonus) : "0";
      form.wagerBonus = filled(bonus.wagerBonus) ? String(bonus.wagerBonus) : "100";
      form.wagerCriteria = toFormValue(bonus.wagerCriteria) || "20";
    }
  }

  if (meta.kind === "INSURANCE") {
    form.insuranceCut = bonus.bonusType === "TWOCUT" ? "TWOCUT" : "ONECUT";
    form.numberOfLegs = toFormValue(bonus.numberOfLegs);
    form.minStake = toFormValue(bonus.minStake);
    form.bonusPayoutPercentage = toFormValue(bonus.bonusPayoutPercentage);
  }

  // freeSpinConfig rows live behind GET /free-spin/{bonusId}/config — the
  // wizard fetches them best-effort after prefilling (see CreateBonusV2).
  // Wallet templates with an optional spin package go the same way; an empty
  // fetch simply leaves the package switched off.
  if (meta.kind === "SPINS" || meta.freeSpins) {
    form.freeSpinConfig = [];
  }

  // Sports vs Games & Casino scope (INT-10950 part 2). Never offered on a
  // spins-only template — its spin rows already name the games.
  if (meta.kind === "MONEY" && meta.betConditions && !meta.spinsOnly) {
    const providers = toNameList(bonus.providerName);
    const games = toNameList(bonus.gameName);
    const codes = toNameList(bonus.gameCode);
    if (bonus.applicableType === "CASINO") {
      form.scopeCategory = "CASINO";
      // The lists are parallel: one row per game, carrying the provider at
      // the same index (a single provider covers every game when the
      // operator picked several games from one provider).
      form.casinoRows = games.map((gameName, index) => ({
        providerName: providers[index] || providers[0] || "",
        subProviderName: "",
        gameName,
        gameCode: codes[index] || "",
      }));
    }
  }

  // Bet-shape conditions. numberOfLegs is the insured accumulator size on
  // BET_INSURANCE (handled above) and the qualifying-bet minimum here.
  if (meta.betConditions) {
    form.numberOfLegs = toFormValue(bonus.numberOfLegs);
    form.oddsThresholdScope = bonus.isOddsThresholdCumulative
      ? "CUMULATIVE"
      : "SELECTION";
  }

  if (bonus.oddsThreshold) form.oddsThreshold = String(bonus.oddsThreshold);
  if (bonus.maxWinPercentage && Number(bonus.maxWinPercentage) < 100) {
    form.maxWinPercent = String(bonus.maxWinPercentage);
  }
  if (bonus.maxWinAmount) form.maxWinAmount = String(bonus.maxWinAmount);
  form.stakeReturned = Boolean(bonus.stakeReturned);

  // Scope — bonusSportSelection rows already carry the shape the wizard uses;
  // the row id belongs to the old bonus and must not be re-posted.
  const scopeRows = Array.isArray(bonus.bonusSportSelection)
    ? bonus.bonusSportSelection
    : [];
  form.scopeRows = scopeRows.map((row) => {
    const copy = { ...row };
    delete copy.id;
    return copy;
  });
  form.markets = Array.isArray(bonus.applicableMarketIds)
    ? bonus.applicableMarketIds.map((marketId) => ({
      marketId,
      marketName: `Market #${marketId}`,
    }))
    : [];

  // Expiry
  if (bonus.applyExpiryHours) {
    form.expiryUnit = "HOURS";
    form.expiryValue = String(bonus.applyExpiryHours);
  } else if (bonus.applyExpiry) {
    form.expiryUnit = "DAYS";
    form.expiryValue = String(bonus.applyExpiry);
  }

  // Safety limits — mirror the original exactly (empty = no cap).
  form.maxGrantsPerPlayer = toFormValue(bonus.maxGrantsPerPlayer);
  form.maxGrantsPerPlayerPerDay = toFormValue(bonus.maxGrantsPerPlayerPerDay);
  form.maxCategoryGrants = toFormValue(bonus.maxCategoryGrants);
  form.categoryWindowDays = toFormValue(bonus.categoryWindowDays);

  return { template, form };
};
